// AR "magic window" (F7, 2026-10-02): the rear camera with nearby pipes,
// valves, hydrants and tobies drawn over it. Design:
// reports/flow_plan/05_field_workspaces.md section 5 step 2.
//
// Plain canvas over a getUserMedia <video> - no WebXR (iOS Safari has
// none), no three.js / LocAR.js: the overlay is a handful of ground
// polylines and pins, and a 2D pinhole projection (arMath.ts, unit-tested
// through its Python mirror field_ar.py) is all it needs.
//
// The SERVER chooses the assets and converts them to local East-North-Up
// metres (/api/field/ar-assets), adds the depth label, network and the
// uncertainty, ranks calibration candidates (/ar-calibrate/candidates),
// computes calibration offsets (/ar-calibrate, /ar-align-road) and runs the
// experimental camera auto-align (/ar-auto-align). The phone only projects
// to the screen at display rate and captures the one frame auto-align needs.
//
// After the first real-phone test (Armitage St, 2026-10-02):
//  * nothing is drawn beyond ~20 m (where the ground is within 4 deg of the
//    horizon and a 1 deg pitch error moves it metres); things fade out from
//    12 m; long mains are clipped instead of running to the horizon;
//  * no uncertainty bands by default - the selected asset gets a faint
//    dashed corridor with true perspective, clamped;
//  * tap anything to see what it is (identify card); one colour per network;
//  * calibrating never asks for an asset ID: tap the lid you see and pick
//    from labelled candidates, or line up with the road, or (experimental)
//    let the server find the kerb in one camera frame.
//
// SAFETY: a persistent banner "Indicative only - not a substitute for
// beforeUdig or a certified cable/pipe locator." sits over the camera view
// at all times, plus a first-use acknowledgement.
//
// Battery: frames are drawn at 5 / 12 / 24 fps (default 12), only when the
// pose changed or once a second; the camera stops when the view closes,
// when "Off" is tapped and when the app goes to the background.
import { showToast } from "../toast";
import { outboxStatus } from "./outbox";
import { uploadBadge } from "./uploadBadge";
import { h, fill } from "./dom";
import type { ArAsset, ArAssets, ArCalCandidate, ArHeadingFix, ArRoadAlt, ArRoadLines, ArRoadRole } from "./fieldApi";
import { fieldApi } from "./fieldApi";
import { getHeading, screenAngle } from "./heading";
import type { Calibration, View } from "./arMath";
import {
  AR_FADE_START_M,
  BAND_MAX_M,
  CAMERA_HEIGHT_M,
  calibrationOffset,
  cameraBasis,
  clipPathToDisc,
  compassErrorDeg,
  corridorEdges,
  effectiveGpsM,
  enuBearing,
  focalPx,
  groundFade,
  lateralUncertaintyM,
  localOffset,
  maxDrawRangeM,
  nearestOnPath,
  projectGroundPoint,
  projectPoint,
  projectPolyline,
  rankCalibrationCandidates,
  groundPointAt,
  rotateEN,
  solveRigid2D,
  subdivide,
  twistPairs,
  unproject,
} from "./arMath";
import type { Pair } from "./arMath";
import { NETWORK_LABELS, assetColour, drawSymbol, hexA, legendEl, symbolIcon, symbolOf } from "./assetStyle";
import type { ArAction, ArMode, CalMode, CalSanity, DirectMode } from "./arCalState";
import {
  TWO_POINT_MIN_SPAN_M,
  UNDO_INLINE_MS,
  calibrationSanity,
  calibrationSummary,
  closePairs,
  directSanity,
  headingSanity,
  kerbSanity,
  autoRoadConfidence,
  lidDirection,
  lidPreviewText,
  nextMode,
  overlaysFor,
  statusPill,
  undoInlineVisible,
} from "./arCalState";
import { GestureClassifier } from "./arGesture";
import type { GestureEvent } from "./arGesture";
import { LocalFrame, anchorStatus, calibrationSigmaM } from "./arTrack";
import { identifyCard } from "./identify";
import type { AnchorToken } from "./track";
import { anchorAlong, anchorShift, forgetAnchor, holdingPosition, onTrackEvent, setHoldPosition, setTrackHeadingOffset, stopOdometry, trackOdometry, undoAnchor } from "./track";
import { discardQueuedScans, loadScanConfig, scanConfig, scanLabel, scanOn, scanStatus, startScan, stopScan } from "./scan";
// camera tracking (experimental) is loaded on demand: type-only here, so it stays out of the boot bundle
type VoModule = typeof import("./visualOdometry");

export interface ArFix {
  lat: number;
  lon: number;
  /** 1-sigma metres: the TRACKED position's uncertainty (track.ts) when tracking, else the raw GPS accuracy */
  accuracy: number;
  /** the raw GPS accuracy (display) */
  gpsAccuracy?: number | null;
}

export interface ArHost {
  getFix: () => ArFix | null;
  /** switch the field app to the radar (no camera / no orientation sensors) */
  fallbackToRadar: (why: string) => void;
  /** the user picked an asset in AR: keep the field app's selection in step */
  onSelect?: (assetId: string | null) => void;
  selected?: () => string | null;
  /** "Back to the map" on the AR toolbar (2026-10-06): leave the camera view */
  exit?: () => void;
  /** AR scan mode (2026-10-14): the organisation's projects, the selected one, and the raw GPS fix */
  projects?: () => { id: string; name: string }[];
  projectId?: () => string | null;
  rawFix?: () => { lat: number; lon: number; accuracy: number | null; at?: number | null } | null;
}

const WARNING = "Indicative only — not a substitute for beforeUdig or a certified cable/pipe locator.";
const WARNING_MORE = "Positions come from the asset register, your phone's GPS (often 5–15 m out) and compass (±10°, worse near cars and steel). Depths are register values, never measured. Do not dig, mark out or excavate from this view: get a beforeUdig plan and use a certified locator.";
/** first-use acknowledgement: once a day (2026-10-06; it was once ever) */
const ACK_KEY = "wcc.field.arAck";
/** how many AR sessions have shown the "hold and drag" hint */
const COACH_KEY = "wcc.field.arCoach";
const FOV_KEY = "wcc.field.arFov";
const FPS_KEY = "wcc.field.arFps";
const CAL_KEY = "wcc.field.arCalibration";
const SENSORS_KEY = "wcc.field.arSensors";
const VO_KEY = "wcc.field.arCameraTracking";
const REFETCH_MOVE_M = 12;
const REFETCH_AGE_MS = 60_000;
// Long-edge FOV of getUserMedia video on typical phones: a 26 mm-equivalent
// main camera is ~67 deg across the sensor's long edge; 16:9 video keeps the
// long edge and stabilisation crops ~5-10 %, so 60-67 deg. 64 is the middle;
// "Zoom +/-" adjusts it (3 deg steps) and is remembered per phone.
const DEFAULT_FOV = 64;
const TAP_POINT_PX = 30;
const TAP_LINE_PX = 22;

/** "Undo last calibration" stays on offer (in the status sheet) this long after an apply / reset */
const UNDO_MS = 10 * 60_000;
/** ... and inline on the screen, as a toast-like action, this long (tests shorten it) */
let undoInlineMs = UNDO_INLINE_MS;
export function setUndoInlineMs(ms: number): void {
  undoInlineMs = ms;
}
export const CALIBRATION_EVENT = "field-ar-calibration";

/** a new drag within this of an earlier one (map metres) replaces it */
const PIN_REPLACE_M = 1.5;

interface StoredCal extends Calibration {
  at: number;
  lat: number;
  lon: number;
  ttl_s: number;
  max_move_m: number;
  asset_id?: string;
  method?: "lid" | "road" | "camera" | "kerb" | "road-camera" | "drag";
  /** 2026-10-06 direct manipulation: which kind */
  direct?: DirectMode;
  label?: string;
  /** 2026-10-05: the position part was handed to the tracker as an anchor
   * (track.ts) - draw() must NOT add east/north on top any more */
  anchored?: boolean;
  /** ... and it carried a position (lid with a ground range, kerb), not just a heading */
  pos_anchor?: boolean;
  /** what it was anchored on, for the amber bar ("manhole", "the kerb") and where */
  short?: string;
  anchor_lat?: number;
  anchor_lon?: number;
  /** kerb: the shift is only along this unit (east, north) normal */
  normal?: [number, number] | null;
  lateral_shift_m?: number;
  sigma_m?: number;
  /** lid calibrations (2026-10-05): "position" = the tap moved the position only */
  mode?: CalMode;
  /** where the direction (heading_offset_deg) comes from: "compass", "road (Washbournes Road)", "hydrant" */
  heading_from?: string;
}

/** A calibration shown on screen before it is applied (Apply / Cancel). */
interface Preview {
  cal: StoredCal;
  sanity: CalSanity;
  title: string;
  /** "Pick another": back to the same candidate list */
  back?: () => void;
  /** where the kerbs should be (LINZ centreline +/- half-width), ENU from (lat, lon): drawn on the ground */
  guide?: { lat: number; lon: number; lines: [number, number][][] };
  /** kerb: offer "Direction only" too */
  directionOnly?: boolean;
  /** camera-assisted road calibration: every line found, the one in use and its role */
  road?: RoadPick;
  /** a lid calibration: its mode and the "Also correct direction" switch */
  lid?: { mode: CalMode; toggle: () => void };
  /** a road calibration: the road in use and "Not this road?" (re-runs with that road) */
  roads?: { name: string; alternatives: ArRoadAlt[]; pick: (key: string) => void };
  /** a drag / twist / two-point calibration (2026-10-06) */
  direct?: DirectState;
}

/** One hold-and-drag: map point P and where the user put it, Q (both
 * data-origin ENU; Q measured through the BASE pose, the calibration that
 * was in force when the direct calibration started). */
interface Pin {
  P: [number, number];
  Q: [number, number];
  short: string;
}

/** The direct-manipulation preview: the correction (rot, t) relative to
 * `base` (p = R(rot) q + t, arMath.solveRigid2D) and how it was made. */
interface DirectState {
  base: StoredCal | null;
  pins: Pin[];
  rot: number;
  t: [number, number];
  /** a two-finger twist set `rot` (kept by a later one-point drag) */
  twisted: boolean;
  /** the last twist's pairs, relative to the device then (sent to the server) */
  twistPairs: Pair[] | null;
  mode: DirectMode;
  residual: number | null;
  span: number | null;
  sanity: CalSanity | null;
  /** "server": /ar-calibrate re-solved it; "phone": offline */
  checked: "server" | "phone" | null;
  serverNote: string | null;
}

interface RoadPick {
  res: ArRoadLines;
  fix: ArFix;
  /** the frame and pose it came from (re-used for "Not this road?") */
  frame?: Blob;
  pose?: { heading: number; beta: number; gamma: number; screen_angle: number };
  /** the request point (raw, tracked) in data-origin ENU: the lines are relative to it */
  capE: number;
  capN: number;
  selected: number;
  role: ArRoadRole;
}

interface KerbTap {
  /** where the tap met the ground in the CALIBRATED view (drawn), data-origin ENU */
  draw: [number, number];
  /** the same tap in the RAW pose (sent to the server), data-origin ENU */
  raw: [number, number];
}

interface Hit {
  a: ArAsset;
  runs?: [number, number][][]; // screen runs (lines)
  head?: [number, number]; // pin head (points)
  ground?: [number, number];
  d: number;
}

function lsGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function lsSet(k: string, v: string): void {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* storage blocked */
  }
}

const A: {
  root: HTMLElement | null;
  video: HTMLVideoElement | null;
  canvas: HTMLCanvasElement | null;
  info: HTMLElement | null;
  panel: HTMLElement | null;
  sheet: HTMLElement | null;
  legend: HTMLElement | null;
  calbar: HTMLElement | null;
  calbarText: string;
  calbarAt: number;
  stream: MediaStream | null;
  host: ArHost | null;
  data: ArAssets | null;
  fetching: boolean;
  fetchedAt: number;
  raf: number | null;
  lastDraw: number;
  lastPoseKey: string;
  fps: number;
  fov: number;
  cal: StoredCal | null;
  preview: Preview | null;
  undo: { prev: StoredCal | null; until: number; label: string; anchor?: AnchorToken | null } | null;
  mode: ArMode;
  kerbTaps: KerbTap[];
  rawDevE: number;
  rawDevN: number;
  menuOpen: boolean;
  busy: boolean;
  sensorSeenAt: number;
  startedAt: number;
  running: boolean;
  view: View | null; // last frame, calibrated
  rawView: View | null; // last frame, uncalibrated (for calibration taps)
  devE: number;
  devN: number;
  hits: Hit[];
  selected: string | null;
  legendOn: boolean;
  sensors: HTMLElement | null;
  sensorsOn: boolean;
  sensorsAt: number;
  /** the last manual road alignment / kerb request (re-run for "Not this road?") */
  lastRoad: { fix: ArFix; heading: number } | null;
  lastKerb: { taps: KerbTap[] } | null;
  /** "Camera tracking (experimental)": on, and the lazily loaded module */
  voOn: boolean;
  vo: VoModule | null;
  // --- 2026-10-06 declutter: slim safety line, status pill, icon toolbar ---
  warn: HTMLElement | null;
  warnOpen: boolean;
  pill: HTMLElement | null;
  pillKey: string;
  undoEl: HTMLElement | null;
  /** when the inline Undo was offered (null: not on screen) */
  undoInlineAt: number | null;
  undoTimer: number | null;
  pop: HTMLElement | null;
  popKind: "calibrate" | "more" | "legend" | null;
  tip: HTMLElement | null;
  badge: HTMLElement | null;
  statusOpen: boolean;
  /** "N within 20 m, M further away": moved from the info line to the status sheet */
  drawNote: string;
  coachUntil: number;
  // --- direct manipulation ---
  gest: GestureClassifier;
  gestTimer: number | null;
  /** the view drawn with the calibration in FORCE (not a preview): drags are measured in it */
  baseView: View | null;
  baseDevE: number;
  baseDevN: number;
  grab: { s0: [number, number]; anchor: [number, number]; P: [number, number]; short: string; prev: DirectState | null } | null;
  twist: { pivot: [number, number]; rot0: number; t0: [number, number]; prev: DirectState | null; pairs: Pair[] | null } | null;
  holdRing: { x: number; y: number; at: number } | null;
  directSeq: number;
  /** AR scan mode's recording indicator (always visible while recording) */
  rec: HTMLElement | null;
} = {
  root: null, video: null, canvas: null, info: null, panel: null, sheet: null, legend: null, calbar: null, calbarText: "", calbarAt: 0,
  stream: null, host: null,
  data: null, fetching: false, fetchedAt: 0, raf: null, lastDraw: 0, lastPoseKey: "", fps: Number(lsGet(FPS_KEY)) || 12,
  fov: Number(lsGet(FOV_KEY)) || DEFAULT_FOV, cal: loadCal(), preview: null, undo: null, mode: "view", kerbTaps: [], rawDevE: 0, rawDevN: 0,
  menuOpen: false, busy: false, sensorSeenAt: 0,
  startedAt: 0, running: false, view: null, rawView: null, devE: 0, devN: 0, hits: [], selected: null,
  // the legend is collapsed to an icon by default (2026-10-06): it was a big always-open panel
  legendOn: false,
  sensors: null, sensorsOn: lsGet(SENSORS_KEY) === "1", sensorsAt: 0, lastRoad: null, lastKerb: null,
  voOn: lsGet(VO_KEY) === "1", vo: null,
  warn: null, warnOpen: false, pill: null, pillKey: "", undoEl: null, undoInlineAt: null, undoTimer: null,
  pop: null, popKind: null, tip: null, badge: null, statusOpen: false, drawNote: "", coachUntil: 0,
  gest: new GestureClassifier(), gestTimer: null, baseView: null, baseDevE: 0, baseDevN: 0, grab: null, twist: null, holdRing: null, directSeq: 0,
  rec: null,
};

function loadCal(): StoredCal | null {
  try {
    const raw = sessionStorage.getItem(CAL_KEY);
    return raw ? (JSON.parse(raw) as StoredCal) : null;
  } catch {
    return null;
  }
}
function saveCal(c: StoredCal | null): void {
  A.cal = c;
  // the tracker's walking direction uses the corrected compass
  setTrackHeadingOffset(c?.heading_offset_deg ?? 0);
  try {
    if (c) sessionStorage.setItem(CAL_KEY, JSON.stringify(c));
    else sessionStorage.removeItem(CAL_KEY);
  } catch {
    /* storage blocked */
  }
  // the field app's 2D map shows the calibrated position as a second marker
  document.dispatchEvent(new CustomEvent(CALIBRATION_EVENT));
}

function calValid(c: StoredCal | null, fix: ArFix | null): boolean {
  if (!c) return false;
  if (Date.now() - c.at > c.ttl_s * 1000) return false;
  if (!fix) return true;
  const [de, dn] = localOffset(c.lat, c.lon, fix.lat, fix.lon);
  return Math.hypot(de, dn) <= c.max_move_m;
}

/** The calibration in force for this fix, or null (no toast, no side
 * effects): the field app's 2D map and its "AR calibration active" chip. */
export function currentCalibration(fix: ArFix | null): StoredCal | null {
  return calValid(A.cal, fix) ? A.cal : null;
}

/** Drop the calibration (kept for "Undo" for a while). */
export function resetArCalibration(): void {
  if (!A.cal) return;
  A.undo = { prev: A.cal, until: Date.now() + UNDO_MS, label: "Undo reset" };
  // the tracker forgets the anchor and re-converges on GPS ("Undo reset"
  // brings the heading and label back, not the anchor)
  if (A.cal.pos_anchor) forgetAnchor();
  saveCal(null);
  A.lastPoseKey = "";
  if (A.root) {
    offerUndo("Calibration reset");
    renderPanel();
    renderCalBar(true);
  } else showToast("AR calibration reset - positions are raw GPS + compass again.");
}

export function canUndoArCalibration(): boolean {
  return !!A.undo && Date.now() < A.undo.until;
}

/** Put back the calibration that was in force before the last apply/reset. */
export function undoArCalibration(): void {
  if (!canUndoArCalibration() || !A.undo) return;
  const prev = A.undo.prev && Date.now() - A.undo.prev.at <= A.undo.prev.ttl_s * 1000 ? A.undo.prev : null;
  // take the last anchor back out of the tracker (keeping the walking since)
  if (A.undo.anchor) undoAnchor(A.undo.anchor);
  A.undo = null;
  saveCal(prev);
  A.lastPoseKey = "";
  hideUndo();
  if (A.statusOpen) showStatusSheet();
  renderPanel();
  renderCalBar(true);
  showToast(prev ? `Back to the previous calibration (${prev.label ?? "a lid"}).` : "Calibration undone - positions are raw GPS + compass again.");
}

/** Calibration still valid for this fix? Expires after its TTL or once
 * the user has walked away from where it was made. */
function activeCal(fix: ArFix | null): StoredCal | null {
  const c = A.cal;
  if (!c || !fix) return null;
  const [de, dn] = localOffset(c.lat, c.lon, fix.lat, fix.lon);
  if (Date.now() - c.at > c.ttl_s * 1000 || Math.hypot(de, dn) > c.max_move_m) {
    saveCal(null);
    showToast("AR calibration expired (time or distance) - calibrate again.");
    renderPanel();
    return null;
  }
  return c;
}

export function arSupported(): { ok: boolean; why: string | null } {
  if (!window.isSecureContext) return { ok: false, why: "The camera view needs HTTPS." };
  if (!navigator.mediaDevices?.getUserMedia) return { ok: false, why: "This browser can't open the camera." };
  if (!("DeviceOrientationEvent" in window)) return { ok: false, why: "This device has no orientation sensors." };
  return { ok: true, why: null };
}

/** Render the AR view into `container` (the field app's view element). */
export function mountAr(container: HTMLElement, host: ArHost): void {
  stopAr();
  A.host = host;
  A.cal = loadCal();
  setTrackHeadingOffset(A.cal?.heading_offset_deg ?? 0);
  A.selected = host.selected?.() ?? null;
  const sup = arSupported();
  if (!sup.ok) {
    fill(container, h("div", { class: "fa-ar-fallback" },
      h("p", { class: "fa-warn" }, `${sup.why} Showing the radar instead.`)));
    window.setTimeout(() => host.fallbackToRadar(sup.why ?? "AR not supported"), 50);
    return;
  }
  if (!ackedToday()) {
    const tick = h("input", { type: "checkbox", class: "fa-check" }) as HTMLInputElement;
    const go = h("button", { class: "fa-btn fa-big fa-primary", disabled: true }, "Open camera view") as HTMLButtonElement;
    tick.addEventListener("change", () => (go.disabled = !tick.checked));
    go.addEventListener("click", () => {
      lsSet(ACK_KEY, String(Date.now()));
      mountAr(container, host);
    });
    fill(container, h("div", { class: "fa-ar-ack" },
      h("h3", {}, "Camera view (AR) - read first"),
      h("p", { class: "fa-warn" }, `⚠ ${WARNING}`),
      h("p", {}, "Pipes and fittings are drawn from the asset register using your phone's GPS (often 5–15 m out) and compass (±10°, worse near cars and steel). Only things within about 20 m are drawn. Tap an asset to see what it is and how far out it could be. Depths are register values where known, never measured."),
      h("p", {}, "Do not dig, mark out or excavate based on this view. Always get a beforeUdig plan and use a certified locator."),
      h("p", { class: "fa-muted fa-small" }, "Asked once a day. Afterwards the warning stays as a slim ⚠ line at the top of the camera view: tap it to read it again."),
      h("label", { class: "fa-row-check" }, tick, h("span", {}, "I understand this view is indicative only")),
      go));
    return;
  }
  A.video = h("video", { class: "fa-ar-video", autoplay: true, muted: true, playsinline: true }) as HTMLVideoElement;
  A.video.setAttribute("webkit-playsinline", "");
  A.video.muted = true;
  A.canvas = h("canvas", { class: "fa-ar-canvas" });
  A.info = h("div", { class: "fa-ar-info", role: "status", hidden: true });
  A.panel = h("div", { class: "fa-ar-panel" });
  A.sheet = h("div", { class: "fa-ar-sheet", hidden: true });
  A.legend = h("div", { class: "fa-ar-legend", hidden: true });
  // the old 6-line amber bar is gone (2026-10-06); kept as a hidden node so nothing else breaks
  A.calbar = h("div", { class: "fa-ar-calbar", hidden: true });
  A.calbarText = "";
  A.sensors = h("div", { class: "fa-ar-sensors", hidden: !A.sensorsOn });
  // 2026-10-06 (iPhone: "the screen gets very busy"): ONE slim, semi-transparent
  // safety line that expands on tap (never removable), a small status pill
  // (anchor + accuracy, tracking dot, compass), an inline Undo for a few
  // seconds after a calibration, and an icon toolbar. Aim: >= 75 % of the
  // screen height is camera when nothing is open.
  A.warnOpen = false;
  A.warn = h("button", { class: "fa-ar-warning", "aria-expanded": "false", "aria-label": `Safety: ${WARNING} Tap to read more.`,
    onclick: () => toggleWarning() });
  A.pill = h("button", { class: "fa-ar-pill", onclick: () => toggleStatusSheet() });
  A.pillKey = "";
  A.undoEl = h("div", { class: "fa-ar-undo", role: "status", hidden: true });
  A.pop = h("div", { class: "fa-ar-pop", hidden: true });
  A.popKind = null;
  A.tip = h("div", { class: "fa-ar-tip", role: "tooltip", hidden: true });
  A.badge = h("div", { class: "fa-ar-badge", "aria-live": "polite", hidden: true });
  A.statusOpen = false;
  A.rec = h("button", { class: "fa-ar-rec", hidden: true, "aria-live": "polite", onclick: () => toggleScan() });
  A.root = h("div", { class: "fa-ar" },
    A.video, A.canvas,
    h("div", { class: "fa-ar-topbar" }, A.warn, A.pill),
    A.rec,
    // row #40: frames (and captures) still to upload, with "Send now", while not recording
    uploadBadge({ className: "fa-upq-ar" }),
    // instructions while calibrating / waiting, and (optionally) the sensor readout
    h("div", { class: "fa-ar-top" }, A.info, A.sensors),
    A.badge,
    // everything at the bottom is one column in normal flow: nothing can overlap the toolbar
    h("div", { class: "fa-ar-bottom" }, A.sheet, A.legend, A.pop, A.undoEl, A.calbar, A.tip, A.panel));
  fill(container, A.root);
  container.closest(".field-app")?.classList.add("fa-ar-immersive");
  renderWarning();
  bindGestures(A.canvas);
  // the "hold and drag" hint: the first three AR sessions, for 8 s
  const seen = Number(lsGet(COACH_KEY)) || 0;
  if (seen < 3) {
    A.coachUntil = Date.now() + 8000;
    lsSet(COACH_KEY, String(seen + 1));
  }
  renderLegend();
  renderPanel();
  renderCalBar(true);
  renderRec();
  void loadScanConfig().then(() => renderPanel());
  void startCamera();
}

/** The first-use acknowledgement holds for the rest of the (local) day. */
function ackedToday(): boolean {
  const at = Number(lsGet(ACK_KEY));
  if (!at || !Number.isFinite(at)) return false;
  return new Date(at).toDateString() === new Date().toDateString();
}

async function startCamera(): Promise<void> {
  if (!A.video) return;
  try {
    A.stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false,
    });
  } catch (e) {
    const why = (e as Error)?.name === "NotAllowedError" ? "Camera permission was refused." : "The camera couldn't be opened.";
    if (A.info) A.info.textContent = `${why} Showing the radar instead.`;
    A.host?.fallbackToRadar(why);
    return;
  }
  A.video.srcObject = A.stream;
  try {
    await A.video.play();
  } catch {
    /* iOS: plays once metadata arrives (muted + playsinline) */
  }
  A.running = true;
  A.startedAt = Date.now();
  A.sensorSeenAt = 0;
  document.addEventListener("visibilitychange", onVisibility);
  loop();
  if (A.voOn) void startVo();
}

/** Camera tracking (experimental, 2026-10-06): optical flow on the ground in
 * the camera feed measures how far the phone moved (visualOdometry.ts,
 * in a Web Worker) and feeds the position filter instead of step counting
 * while it works. Loaded only when switched on. */
async function startVo(): Promise<void> {
  if (!A.vo) {
    try {
      A.vo = await import("./visualOdometry");
    } catch {
      showToast("Camera tracking couldn't load (offline?). Position follows GPS + your steps.");
      A.voOn = false;
      renderPanel();
      return;
    }
  }
  const video = A.video;
  if (!A.voOn || !video || !A.running) return;
  A.vo.startVisualOdometry({
    video,
    pose: () => {
      const hd = getHeading();
      if (hd.heading === null || hd.pitch === null || hd.roll === null) return null;
      return { heading: hd.heading, beta: hd.pitch + 90, gamma: hd.roll, screenAngle: screenAngle() };
    },
    fov: () => A.fov,
    height: () => A.data?.camera_height_m ?? CAMERA_HEIGHT_M,
    onResult: (r) => trackOdometry(r),
  });
  renderCalBar(true);
}

function stopVo(): void {
  A.vo?.stopVisualOdometry();
  stopOdometry();
}

/** "Camera tracking: good / weak / lost" for the amber bar, or null when off. */
function voLine(): { text: string; level: "good" | "weak" | "lost" } | null {
  if (!A.voOn) return null;
  const st = A.vo?.voStatus();
  if (!st || !st.on || st.quality === "starting") return { text: "Camera tracking: starting…", level: "weak" };
  if (performance.now() - st.at > 3000) return { text: "Camera tracking: lost (no frames) · using your steps", level: "lost" };
  if (st.quality === "good") return { text: "Camera tracking: good", level: "good" };
  if (st.quality === "weak") return { text: `Camera tracking: weak${st.why ? ` (${st.why})` : ""}`, level: "weak" };
  return { text: `Camera tracking: lost${st.why ? ` (${st.why})` : ""} · using your steps`, level: "lost" };
}

function onVisibility(): void {
  if (document.visibilityState === "hidden") {
    // the camera must not run in the background (battery, privacy)
    if (scanOn()) {
      stopScan();
      showToast("Scan mode switched off: the app went to the background.");
    }
    A.stream?.getTracks().forEach((t) => t.stop());
    A.stream = null;
    A.running = false;
    stopVo();
    if (A.raf !== null) cancelAnimationFrame(A.raf);
    A.raf = null;
  } else if (A.root?.isConnected && !A.stream) {
    void startCamera();
  }
}

export function stopAr(): void {
  A.running = false;
  // scan mode never outlives the camera view (frames leave the phone only while it is visibly on)
  stopScan();
  stopVo();
  if (A.raf !== null) cancelAnimationFrame(A.raf);
  A.raf = null;
  A.stream?.getTracks().forEach((t) => t.stop());
  A.stream = null;
  if (A.video) A.video.srcObject = null;
  document.removeEventListener("visibilitychange", onVisibility);
  A.root?.closest(".field-app")?.classList.remove("fa-ar-immersive");
  document.querySelectorAll(".field-app.fa-ar-immersive").forEach((el) => el.classList.remove("fa-ar-immersive"));
  if (A.undoTimer !== null) window.clearTimeout(A.undoTimer);
  if (A.gestTimer !== null) window.clearTimeout(A.gestTimer);
  A.undoTimer = A.gestTimer = null;
  A.undoInlineAt = null;
  A.grab = A.twist = null;
  A.gest = new GestureClassifier();
  A.root = A.video = A.canvas = A.info = A.panel = A.sheet = A.legend = A.calbar = A.sensors = null;
  A.warn = A.pill = A.undoEl = A.pop = A.tip = A.badge = A.rec = null;
  A.popKind = null;
  A.statusOpen = false;
  A.mode = nextMode(A.mode, "stop");
  A.preview = null;
  A.kerbTaps = [];
  A.menuOpen = false;
  A.hits = [];
}

// "Hold position" lets go by itself once you walk off
onTrackEvent((e) => {
  if (e !== "hold-released" || !A.root) return;
  showToast("Hold released: you're walking. Position follows GPS + your steps again.");
  renderPanel();
  renderCalBar(true);
});

export function arRunning(): boolean {
  return A.running;
}

/** For the layout harness (frontend/tests/harness): the mode, and where an
 * asset was last drawn (a pin's head, or its ground point). Read-only. */
export function arProbe(): { mode: () => ArMode; pinOf: (id: string, ground?: boolean) => [number, number] | null } {
  return {
    mode: () => A.mode,
    pinOf: (id, ground = false) => {
      const hit = A.hits.find((x) => x.a.asset_id === id);
      if (!hit) return null;
      const p = ground ? hit.ground : hit.head;
      if (p) return [p[0], p[1]];
      // a line: its drawn vertex nearest the left quarter of the screen
      const W = A.view?.width ?? 0, H = A.view?.height ?? 0;
      let best: [number, number] | null = null;
      for (const run of hit.runs ?? []) {
        for (const q of run) {
          if (q[0] < 20 || q[0] > W - 20) continue;
          if (!best || Math.hypot(q[0] - W / 4, q[1] - H / 2) < Math.hypot(best[0] - W / 4, best[1] - H / 2)) best = [q[0], q[1]];
        }
      }
      return best;
    },
  };
}

// ---------------------------------------------------------------------------
// Panel, legend, sheets
// ---------------------------------------------------------------------------

function chip(label: string, onclick: () => void, on = false, title?: string): HTMLElement {
  return h("button", { class: `fa-chip${on ? " on" : ""}`, onclick, title }, label);
}

// --- icons (2026-10-06): plain stroke icons, white on the dark toolbar ------

const ICONS: Record<string, string> = {
  map: '<path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z"/><path d="M9 4v14M15 6v14"/>',
  calibrate: '<circle cx="12" cy="12" r="7"/><path d="M12 2v4M12 18v4M2 12h4M18 12h4"/><circle cx="12" cy="12" r="1.6" fill="currentColor"/>',
  track: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
  hold: '<rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  legend: '<path d="m12 3 9 5-9 5-9-5 9-5z"/><path d="m3 13 9 5 9-5"/>',
  sensors: '<path d="M3 12h4l3-8 4 16 3-8h4"/>',
  more: '<circle cx="5" cy="12" r="1.7" fill="currentColor"/><circle cx="12" cy="12" r="1.7" fill="currentColor"/><circle cx="19" cy="12" r="1.7" fill="currentColor"/>',
  anchor: '<circle cx="12" cy="5" r="2"/><path d="M12 7v14M5 12H3a9 9 0 0 0 18 0h-2M8 10h8"/>',
  compass: '<circle cx="12" cy="12" r="9"/><path d="m15.5 8.5-2 5-5 2 2-5 5-2z"/>',
  gps: '<circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="8"/>',
  direction: '<path d="M20 12a8 8 0 1 1-2.3-5.7"/><path d="M20 4v5h-5"/>',
  scan: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4" fill="currentColor"/>',
  hand: '<path d="M8 13V5.5a1.5 1.5 0 0 1 3 0V12M11 11V4.5a1.5 1.5 0 0 1 3 0V12M14 11.5V6a1.5 1.5 0 0 1 3 0v8a7 7 0 0 1-7 7h-.5a6 6 0 0 1-4.6-2.2L2.6 15.4a1.5 1.5 0 0 1 2.3-1.9L8 16"/>',
};

function icon(name: string, size = 22): HTMLElement {
  const s = h("span", { class: "fa-ico-svg", "aria-hidden": "true" });
  s.innerHTML = `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${ICONS[name] ?? ""}</svg>`;
  return s;
}

/** A toolbar button: an icon with an aria-label; a long press (or hover)
 * shows the label as a tooltip instead of acting. */
function tbtn(name: string, label: string, action: string, onclick: () => void, on = false): HTMLElement {
  const b = h("button", { class: `fa-ar-tb${on ? " on" : ""}`, "aria-label": label, title: label, "data-action": action, "aria-pressed": on ? "true" : "false" },
    icon(name));
  let timer: number | null = null;
  let shown = false;
  b.addEventListener("pointerdown", () => {
    shown = false;
    timer = window.setTimeout(() => {
      shown = true;
      showTip(b, label);
    }, 450);
  });
  const clear = () => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  };
  b.addEventListener("pointerup", clear);
  b.addEventListener("pointerleave", clear);
  b.addEventListener("pointercancel", clear);
  b.addEventListener("contextmenu", (e) => e.preventDefault());
  b.addEventListener("click", () => {
    if (shown) {
      shown = false;
      return; // the long press only showed the label
    }
    hideTip();
    onclick();
  });
  return b;
}

function showTip(anchor: HTMLElement, text: string): void {
  const tip = A.tip;
  if (!tip || !A.root) return;
  tip.textContent = text;
  tip.hidden = false;
  const r = anchor.getBoundingClientRect(), root = A.root.getBoundingClientRect();
  tip.style.left = `${Math.max(8, Math.min(root.width - 8 - tip.offsetWidth, r.left - root.left + r.width / 2 - tip.offsetWidth / 2))}px`;
  window.setTimeout(hideTip, 1600);
}

function hideTip(): void {
  if (A.tip) A.tip.hidden = true;
}

function exitToMap(): void {
  const host = A.host;
  stopAr();
  if (host?.exit) host.exit();
  else host?.fallbackToRadar("AR switched off");
}

function toggleHold(): void {
  const on = !holdingPosition();
  setHoldPosition(on);
  showToast(on ? "Position held: only the direction updates. Walk off (or tap again) to release." : "Position follows GPS + your steps again.");
  renderPanel();
  renderCalBar(true);
}

function toggleVo(): void {
  A.voOn = !A.voOn;
  lsSet(VO_KEY, A.voOn ? "1" : "0");
  if (A.voOn) {
    showToast("Camera tracking on: the phone measures your movement from the ground in the camera view. Keep some ground in view. Nothing is sent or kept.");
    void startVo();
  } else stopVo();
  renderPanel();
  renderCalBar(true);
}

// --- AR scan mode (2026-10-14, reports/ar_scan_mode_spec_2026-10-14.md) -----

function scanHost(): Parameters<typeof startScan>[0] {
  return {
    video: () => A.video,
    gps: () => A.host?.rawFix?.() ?? null,
    fov: () => A.fov,
    calibration: () => {
      const c = currentCalibration(A.host?.getFix() ?? null);
      return c ? { heading_offset_deg: c.heading_offset_deg, east_m: c.east_m, north_m: c.north_m } : null;
    },
    vo: () => ({ on: A.voOn, quality: A.voOn ? (A.vo?.voStatus().quality ?? null) : null }),
    onChange: () => renderRec(),
  };
}

/** Off by default; the toolbar button opens the start sheet (or the stop sheet while recording). */
function toggleScan(): void {
  closePop();
  if (scanOn()) {
    const st = scanStatus();
    openSheet(
      h("h3", {}, "Scan mode is recording"),
      h("p", {}, `Project: ${st.project}. ${scanLabel()}.`),
      h("p", { class: "fa-muted fa-small" }, "Frames already taken keep uploading after you stop (also later, when you have signal)."),
      h("button", { class: "fa-btn fa-big fa-primary", onclick: () => { stopScan(); closeSheet(); renderPanel(); void stoppedToast(); } }, "Stop recording"),
      chip("Keep recording", () => closeSheet()));
    return;
  }
  const cfg = scanConfig();
  const projects = A.host?.projects?.() ?? [];
  // 2026-10-15: a scan is stored with a PROJECT or as a named general SITE;
  // either way it joins an organisation-shared site (nearby suggestions), so
  // everything scanned at a place can be browsed together.
  let kind: "project" | "site" = projects.length ? "project" : "site";
  let chosenSite: number | null = null;
  let freshSite = false;
  let stalePending = false;
  const sel = h("select", { class: "fa-input", "aria-label": "Project" },
    ...projects.map((p) => h("option", { value: p.id }, p.name))) as HTMLSelectElement;
  const pid = A.host?.projectId?.() ?? null;
  if (pid && projects.some((p) => p.id === pid)) sel.value = pid;
  const label = h("input", { class: "fa-input", type: "text", maxlength: "120", placeholder: "Site (optional), e.g. BFP 41202 and surroundings" }) as HTMLInputElement;
  const siteName = h("input", { class: "fa-input", type: "text", maxlength: "120", placeholder: "e.g. Washbournes Rd BFP 41202", "aria-label": "Site name" }) as HTMLInputElement;
  const capture = h("select", { class: "fa-input", "aria-label": "Capture type" },
    h("option", { value: "auto" }, "Automatic (during works if the project is in construction)"),
    h("option", { value: "normal" }, "Normal"),
    h("option", { value: "as_built" }, "During works / as-built (open trenches, exposed pipes)")) as HTMLSelectElement;
  const kindBox = h("div", { class: "fa-seg", role: "radiogroup", "aria-label": "Store the scan with" });
  const projBox = h("div", {});
  const siteBox = h("div", {});
  const suggestBox = h("div", { class: "fa-scan-suggest", "aria-live": "polite" });
  const go = h("button", { class: "fa-btn fa-big fa-primary" }, "Start recording") as HTMLButtonElement;
  const gps = A.host?.rawFix?.() ?? null;

  const ready = (): boolean => {
    if (stalePending) return false;
    if (kind === "project") return projects.some((x) => x.id === sel.value);
    return chosenSite !== null || siteName.value.trim().length >= 3;
  };
  const refresh = (): void => {
    projBox.hidden = kind !== "project";
    siteBox.hidden = kind !== "site";
    for (const b of Array.from(kindBox.querySelectorAll("button"))) b.classList.toggle("on", b.dataset.kind === kind);
    go.disabled = !ready();
  };
  const renderSuggestions = (rows: SiteSuggestion[]): void => {
    const best = rows.find((r) => r.match);
    if (!rows.length) {
      fill(suggestBox, gps ? h("p", { class: "fa-muted fa-small" }, "No scanned sites nearby: this starts a new site.") : null);
      stalePending = false;
      refresh();
      return;
    }
    if (chosenSite === null && !freshSite && best && !best.stale) chosenSite = best.id;
    stalePending = !!best && best.stale === true && chosenSite === null && !freshSite;
    const items = rows.map((r) => {
      const on = chosenSite === r.id;
      const b = h("button", { class: `fa-cand${on ? " on" : ""}`, type: "button" },
        h("span", { class: "fa-cand-main" },
          h("strong", {}, `${on ? "✓ " : ""}${r.name}`),
          h("small", {}, `${Math.round(r.distance_m ?? 0)} m away · ${r.n_sessions} visit(s) · last scanned ${(r.last_scanned_at ?? r.last_visit_at ?? "").slice(0, 10)}${r.has_as_built ? " · has as-built" : ""}`)));
      b.addEventListener("click", () => {
        chosenSite = r.id;
        freshSite = false;
        stalePending = false;
        renderSuggestions(rows);
      });
      return b;
    });
    const fresh = h("button", { class: `fa-cand${freshSite ? " on" : ""}`, type: "button" },
      h("span", { class: "fa-cand-main" }, h("strong", {}, `${freshSite ? "✓ " : ""}Start a fresh site`), h("small", {}, "Don't add to the sites above")));
    fresh.addEventListener("click", () => {
      chosenSite = null;
      freshSite = true;
      stalePending = false;
      renderSuggestions(rows);
    });
    const warn = best?.stale && best.stale_warning
      ? h("p", { class: "fa-warn" }, best.stale_warning.replace("Add as a new visit (recommended) or start a fresh site.", ""),
        h("br", {}), "Add as a new visit (recommended) or start a fresh site:")
      : null;
    fill(suggestBox, h("p", { class: "fa-small" }, "Scanned sites nearby: add this as a new visit, so everything here is browsable together."),
      warn, ...items, fresh);
    refresh();
  };
  let timer: number | null = null;
  const loadSuggestions = (): void => {
    if (!gps) {
      fill(suggestBox, h("p", { class: "fa-muted fa-small" }, "No GPS fix yet: the server will match a nearby site with the same name when the scan uploads."));
      return;
    }
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => void (async () => {
      const nm = kind === "site" ? siteName.value.trim() : label.value.trim() || (projects.find((x) => x.id === sel.value)?.name ?? "");
      try {
        const res = await fetch(`/api/field/scans/sites/suggest?lat=${gps.lat}&lon=${gps.lon}${nm ? `&name=${encodeURIComponent(nm)}` : ""}`, { credentials: "same-origin" });
        if (!res.ok) return;
        renderSuggestions(((await res.json()) as { suggestions: SiteSuggestion[] }).suggestions);
      } catch {
        fill(suggestBox, h("p", { class: "fa-muted fa-small" }, "Offline: the server will match a nearby site with the same name when the scan uploads."));
      }
    })(), 350);
  };
  for (const [k, text] of [["project", "Project"], ["site", "Site"]] as const) {
    const b = h("button", { class: "fa-chip", type: "button", role: "radio", "data-kind": k }, text) as HTMLButtonElement;
    b.addEventListener("click", () => {
      kind = k;
      chosenSite = null;
      freshSite = false;
      refresh();
      loadSuggestions();
    });
    kindBox.append(b);
  }
  sel.addEventListener("change", () => {
    refresh();
    loadSuggestions();
  });
  label.addEventListener("input", () => loadSuggestions());
  siteName.addEventListener("input", () => {
    refresh();
    loadSuggestions();
  });
  projBox.append(projects.length
    ? h("label", { class: "fa-field" }, h("span", {}, "Project"), sel)
    : h("p", { class: "fa-warn" }, "No projects here: pick one in the Projects tab, or record a general Site scan."),
    h("label", { class: "fa-field" }, h("span", {}, "Site label (optional)"), label));
  siteBox.append(h("label", { class: "fa-field" }, h("span", {}, "Site name"), siteName),
    h("p", { class: "fa-muted fa-small" }, "The name plus the phone's GPS locate the site. Others in your organisation will see it on the map."));
  go.addEventListener("click", () => {
    if (!ready()) return;
    const p = projects.find((x) => x.id === sel.value) ?? null;
    const ct = capture.value as "auto" | "normal" | "as_built";
    startScan(scanHost(), {
      kind, project: kind === "project" ? p : null, siteId: chosenSite, newSite: freshSite,
      siteName: kind === "site" ? siteName.value.trim() : null, siteLabel: kind === "project" ? label.value.trim() || null : null,
      captureType: kind === "site" && ct === "auto" ? "normal" : ct,
    });
    closeSheet();
    renderPanel();
    showToast(ct === "as_built"
      ? "Scan mode on: recording an as-built scan. It's kept as a separate, permanent record of the works."
      : "Scan mode on: recording. Walk slowly round the site and keep the assets in view.");
  });
  refresh();
  loadSuggestions();
  openSheet(
    h("h3", {}, "Scan mode"),
    h("p", {}, "While scan mode is on, the phone sends a camera frame every second or two (with its position and direction) to WRAP. Nothing is sent while it is off."),
    h("p", { class: "fa-small" }, cfg.privacy ?? "Faces and number plates are blurred on the server before anything is stored. Frames are kept with the project, like other project records."),
    cfg.model && !cfg.model.available
      ? h("p", { class: "fa-muted fa-small" }, "Site models aren't built yet: this records and keeps the frames for when they are.")
      : null,
    h("div", { class: "fa-field" }, h("span", {}, "Store with"), kindBox),
    projBox,
    siteBox,
    h("label", { class: "fa-field" }, h("span", {}, "Capture"), capture),
    suggestBox,
    go,
    chip("Cancel", () => closeSheet()));
}

interface SiteSuggestion {
  id: number;
  name: string;
  distance_m?: number;
  match?: boolean;
  similarity?: number | null;
  n_sessions: number;
  last_visit_at: string | null;
  last_scanned_at?: string | null;
  stale?: boolean;
  stale_warning?: string | null;
  has_as_built?: boolean;
}

function renderRec(): void {
  const el = A.rec;
  if (!el) return;
  const st = scanStatus();
  el.hidden = !st.on;
  if (!st.on) {
    if (A.panel?.querySelector('[data-action="scan"].on')) renderPanel();
    return;
  }
  el.classList.toggle("warn", st.level !== "ok" || !!st.error);
  fill(el, h("span", { class: "fa-ar-rec-dot", "aria-hidden": "true" }), h("span", {}, scanLabel()),
    st.error ? h("span", { class: "fa-ar-rec-err" }, ` · ${st.error}`) : null);
  el.setAttribute("aria-label", `Scan mode recording: ${scanLabel()}. Tap to stop.`);
}

/** After "Stop recording": say how much is still to send (row #40: on iOS
 * uploads stop while the app is closed, and nothing said so on 15 Oct). */
async function stoppedToast(): Promise<void> {
  const st = await outboxStatus().catch(() => null);
  if (!st?.scanFrames) {
    showToast("Scan stopped.");
    return;
  }
  showToast(st.backgroundSync
    ? `Scan stopped. ${st.scanFrames} frames still to upload: they go when there is signal.`
    : `Scan stopped. ${st.scanFrames} frames still to upload: keep WRAP open until the badge clears.`);
}

/** For "Waiting to upload" (fieldApp.ts): throw away queued scan frames. */
export async function discardScanQueue(): Promise<number> {
  return await discardQueuedScans();
}

function toggleSensors(): void {
  A.sensorsOn = !A.sensorsOn;
  lsSet(SENSORS_KEY, A.sensorsOn ? "1" : "0");
  if (A.sensors) A.sensors.hidden = !A.sensorsOn;
  renderSensors(true);
  renderPanel();
}

/** The bottom of the screen: the icon toolbar in the plain view, a compact
 * action row while calibrating. */
function renderPanel(): void {
  if (!A.panel) return;
  const panel = overlaysFor(A.mode).panel;
  A.panel.classList.toggle("fa-ar-toolbar", panel === "main");
  A.panel.hidden = false;
  if (panel === "road") {
    // camera-assisted (default): the server finds the kerb / lane lines in one frame
    fill(A.panel,
      autoWeak ? h("button", { class: "fa-btn fa-primary", onclick: () => startKerbMode() }, "Tap the kerb instead") : null,
      h("button", { class: `fa-btn${autoWeak ? "" : " fa-primary"}`, disabled: A.busy, onclick: () => void findRoadLines() },
        A.busy ? "Looking for the kerb…" : autoWeak ? "Look again" : "Find the kerb"),
      chip("Manual guides", () => setMode("manual-guides")),
      chip("Cancel", () => setMode("cancel")));
    return;
  }
  if (panel === "road-manual") {
    fill(A.panel,
      h("button", { class: "fa-btn fa-primary", disabled: A.busy, onclick: () => void alignToRoad() }, A.busy ? "Checking…" : "Lined up"),
      chip("Cancel", () => setMode("cancel")));
    return;
  }
  if (panel === "kerb") {
    fill(A.panel,
      h("span", { class: "fa-ar-step" }, A.kerbTaps.length === 0 ? "Tap 1 of 2: the kerb beside you" : "Tap 2 of 2: the same kerb further along"),
      A.kerbTaps.length ? chip("Undo tap", () => { A.kerbTaps.pop(); A.lastPoseKey = ""; renderPanel(); setInfo(); }) : null,
      chip("Cancel calibration", () => setMode("cancel"), true));
    return;
  }
  if (panel === "cal-tap") {
    fill(A.panel, chip("Cancel calibration", () => setMode("cancel"), true));
    return;
  }
  if (panel === "preview") {
    // Apply / Cancel live on the (compact) preview sheet
    fill(A.panel);
    A.panel.hidden = true;
    return;
  }
  fill(A.panel,
    tbtn("map", "Back to the map", "exit", () => exitToMap()),
    tbtn("calibrate", A.cal ? "Recalibrate" : "Calibrate", "calibrate", () => togglePop("calibrate"), A.popKind === "calibrate"),
    tbtn("track", A.voOn ? "Camera tracking (experimental): on" : "Camera tracking (experimental): off", "track", () => toggleVo(), A.voOn),
    tbtn("hold", holdingPosition() ? "Holding position (tap to release)" : "Hold position", "hold", () => toggleHold(), holdingPosition()),
    scanConfig().enabled
      ? tbtn("scan", scanOn() ? "Scan mode: recording (tap to stop)" : "Scan mode: off (tap to record a scan)", "scan", () => toggleScan(), scanOn())
      : null,
    tbtn("legend", "Legend", "legend", () => togglePop("legend"), A.popKind === "legend"),
    tbtn("sensors", "Sensor details", "sensors", () => toggleSensors(), A.sensorsOn),
    tbtn("more", "More: frame rate, zoom, radar, safety", "more", () => togglePop("more"), A.popKind === "more"),
  );
}

/** One popover above the toolbar at a time: the calibrate menu, the "⋯"
 * menu or the legend. Tapping its button again (or the camera) closes it. */
function togglePop(kind: "calibrate" | "more" | "legend"): void {
  A.popKind = A.popKind === kind ? null : kind;
  if (A.popKind) {
    closeSheet();
    A.statusOpen = false;
  }
  renderPop();
  renderPanel();
}

function closePop(): void {
  if (!A.popKind) return;
  A.popKind = null;
  renderPop();
  renderPanel();
}

function renderPop(): void {
  const pop = A.pop;
  if (!pop) return;
  const kind = A.popKind;
  pop.hidden = !kind;
  pop.classList.toggle("fa-ar-pop-legend", kind === "legend");
  if (!kind) {
    fill(pop);
    return;
  }
  if (kind === "legend") {
    fill(pop, legendEl(true));
    return;
  }
  if (kind === "calibrate") {
    // 2026-10-08: the two kerb taps are the default ("excellent" in the field);
    // the camera's automatic kerb finder is an optional "Try automatic", last
    fill(pop,
      h("button", { class: "fa-ar-mi fa-ar-mi-primary", onclick: () => startKerbMode() },
        h("strong", {}, "Tap the kerb in two places (recommended)"),
        h("small", {}, "Stand on the footpath. Tap the kerb edge beside you, then the same kerb 5-15 m further along. Fixes the direction and your position across the road.")),
      h("div", { class: "fa-ar-hint" }, icon("hand", 26),
        h("div", {},
          h("strong", {}, "Or hold and drag the pipes onto where they really are"),
          h("small", {}, "Press on a pipe or lid for a moment, then drag. Twist with two fingers to turn. Drag a second thing too to fix the direction."))),
      h("button", { class: "fa-ar-mi", onclick: () => setMode("menu-tap") }, "Tap a lid or meter I can see (pick from a list)"),
      h("button", { class: "fa-ar-mi fa-ar-mi-quiet", onclick: () => startRoadMode() }, "Try automatic (the camera looks for the kerb - less reliable)"),
      A.cal ? h("button", { class: "fa-ar-mi fa-ar-mi-quiet", onclick: () => { closePop(); resetArCalibration(); } }, "Reset calibration") : null);
    return;
  }
  fill(pop,
    h("button", { class: "fa-ar-mi", onclick: () => { cycleFps(); renderPop(); } },
      `Frame rate: ${A.fps <= 5 ? "saver 5 fps" : A.fps >= 24 ? "smooth 24 fps" : "12 fps"} (tap to change)`),
    h("div", { class: "fa-ar-mi-row" },
      h("span", { class: "fa-small" }, `Camera width ${A.fov}°`),
      h("button", { class: "fa-chip", "aria-label": "Zoom out: overlay too spread out", onclick: () => setFov(A.fov + 3) }, "Zoom −"),
      h("button", { class: "fa-chip", "aria-label": "Zoom in: overlay too squeezed", onclick: () => setFov(A.fov - 3) }, "Zoom +")),
    h("button", { class: "fa-ar-mi", onclick: () => { stopAr(); A.host?.fallbackToRadar("AR switched off"); } }, "Switch the camera off (radar view)"),
    h("button", { class: "fa-ar-mi", onclick: () => { closePop(); setWarning(true); } }, "⚠ Safety information"));
}

// --- the slim safety line ---------------------------------------------------

function setWarning(open: boolean): void {
  A.warnOpen = open;
  renderWarning();
}

function toggleWarning(): void {
  setWarning(!A.warnOpen);
}

/** Never removable: collapsed it is one slim line ("⚠ Indicative only — not
 * a substitute for beforeUdig …"), open it is the whole warning. */
function renderWarning(): void {
  const w = A.warn;
  if (!w) return;
  w.classList.toggle("open", A.warnOpen);
  w.setAttribute("aria-expanded", A.warnOpen ? "true" : "false");
  fill(w,
    h("span", { class: "fa-ar-warning-line" }, `⚠ ${WARNING}`),
    A.warnOpen ? h("span", { class: "fa-ar-warning-more" }, WARNING_MORE) : null);
}

// --- the status pill + its sheet ----------------------------------------------

function pillState(): ReturnType<typeof statusPill> | null {
  const fix = A.host?.getFix() ?? null;
  if (!fix) return null;
  const cal = currentCalibration(fix);
  const anchor = anchorLine(fix, cal);
  const hd = getHeading();
  const vo = voLine();
  return statusPill({
    calibrated: !!cal, posAnchor: !!cal?.pos_anchor, level: anchor?.level ?? "gps", sigma: fix.accuracy,
    headingOffset: cal?.heading_offset_deg ?? 0,
    compass: hd.heading === null ? "none" : hd.disturbed ? "disturbed" : hd.details?.ref === "provisional" ? "provisional" : "ok",
    vo: vo ? vo.level : null,
  });
}

function renderPill(force = false): void {
  const el = A.pill;
  if (!el) return;
  const p = pillState();
  const key = p ? `${p.icon}|${p.text}|${p.tone}|${p.compass}|${p.vo}` : "none";
  if (!force && key === A.pillKey) return;
  A.pillKey = key;
  el.className = `fa-ar-pill tone-${p?.tone ?? "plain"}`;
  el.setAttribute("aria-label", p?.label ?? "Waiting for GPS. Tap for details.");
  el.setAttribute("aria-expanded", A.statusOpen ? "true" : "false");
  if (!p) {
    fill(el, icon("gps", 16), h("span", {}, "GPS…"));
    return;
  }
  fill(el,
    icon(p.icon, 16),
    h("span", { class: "fa-ar-pill-text" }, p.text),
    p.vo ? h("span", { class: `fa-ar-dot fa-ar-dot-${p.vo}`, title: `Camera tracking ${p.vo}` }) : null,
    h("span", { class: `fa-ar-pill-compass c-${p.compass}` }, icon("compass", 16)));
}

function toggleStatusSheet(): void {
  if (A.statusOpen) {
    A.statusOpen = false;
    closeSheet();
    renderPill(true);
    return;
  }
  showStatusSheet();
}

/** Tapping the pill: the detail the amber bar used to show all the time,
 * plus Undo / Reset / Recalibrate. */
function showStatusSheet(): void {
  if (A.mode !== "view") return;
  closePop();
  const fix = A.host?.getFix() ?? null;
  const cal = currentCalibration(fix);
  const anchor = anchorLine(fix, cal);
  const hd = getHeading();
  const vo = voLine();
  const undo = canUndoArCalibration() && A.undo ? A.undo.label : null;
  const compassErr = calCompassErr(hd.accuracy);
  const pos = !fix ? "Waiting for GPS…"
    : fix.gpsAccuracy != null && Math.abs(fix.gpsAccuracy - fix.accuracy) >= 0.5
      ? `Position ±${fix.accuracy.toFixed(1)} m (GPS ±${Math.round(fix.gpsAccuracy)} m)` : `GPS ±${Math.round(fix.accuracy)} m`;
  const compass = hd.heading === null ? "No compass reading"
    : hd.disturbed ? `⚠ ${hd.note ?? "Compass disturbed: move away from cars / metal"}`
      : `Compass ±${Math.round(compassErr)}°${hd.details?.ref === "provisional" ? " (settling: tilt the phone down at the ground for a second)" : ""}`;
  openSheet(
    h("div", { class: "fa-ar-sheet-head" },
      h("strong", {}, cal ? "Calibrated" : "Not calibrated"),
      h("button", { class: "fa-link fa-idcard-close", "aria-label": "Close", "data-close": "1", onclick: () => toggleStatusSheet() }, "✕")),
    h("ul", { class: "fa-ar-status-list" },
      cal ? h("li", {}, `${calibrationSummary(cal, Date.now())}${cal.label ? ` (${cal.label})` : ""}`) : null,
      anchor ? h("li", {}, anchor.text) : null,
      h("li", {}, pos),
      h("li", { class: hd.disturbed ? "fa-warn-inline" : "" }, compass),
      vo ? h("li", {}, h("span", { class: `fa-ar-vo fa-ar-vo-${vo.level}` }, vo.text)) : null,
      A.drawNote ? h("li", { class: "fa-muted" }, A.drawNote) : null),
    h("div", { class: "fa-row" },
      undo ? h("button", { class: "fa-btn", onclick: () => undoArCalibration() }, undo) : null,
      cal ? h("button", { class: "fa-btn", onclick: () => { resetArCalibration(); showStatusSheet(); } }, "Reset") : null,
      h("button", { class: "fa-btn fa-primary", onclick: () => { toggleStatusSheet(); togglePop("calibrate"); } }, cal ? "Recalibrate…" : "Calibrate…")));
  // after openSheet, which clears it for every other sheet
  A.statusOpen = !A.sheet?.hidden;
  renderPill(true);
}

// --- inline Undo (a toast-like action for UNDO_INLINE_MS) ---------------------

function offerUndo(what: string): void {
  A.undoInlineAt = Date.now();
  renderUndo(what);
  if (A.undoTimer !== null) window.clearTimeout(A.undoTimer);
  A.undoTimer = window.setTimeout(() => {
    A.undoTimer = null;
    renderUndo();
  }, undoInlineMs + 20);
}

function hideUndo(): void {
  A.undoInlineAt = null;
  renderUndo();
}

let undoWhat = "";
function renderUndo(what?: string): void {
  const el = A.undoEl;
  if (!el) return;
  if (what !== undefined) undoWhat = what;
  const show = undoInlineVisible(Date.now(), A.undoInlineAt, undoInlineMs) && canUndoArCalibration() && A.mode === "view";
  el.hidden = !show;
  if (!show) {
    fill(el);
    return;
  }
  fill(el, h("span", {}, `✓ ${undoWhat}`), h("button", { class: "fa-chip", onclick: () => undoArCalibration() }, "Undo"));
}

/** Kept for its old callers: the pill (and the inline Undo) replace the amber bar. */
function renderCalBar(force = false): void {
  renderPill(force);
  if (force) renderUndo();
}

/** The anchor state for the amber bar: "Anchored to manhole 6 m away
 * (±1.1 m)" / "Anchor fading" / "Drifting: recalibrate" / "Position held",
 * plus a compass warning. */
function anchorLine(fix: ArFix | null, cal: StoredCal | null): ReturnType<typeof anchorStatus> | null {
  if (!fix) return null;
  let dist: number | null = null;
  if (cal && cal.anchor_lat !== undefined && cal.anchor_lon !== undefined) {
    const [de, dn] = localOffset(fix.lat, fix.lon, cal.anchor_lat, cal.anchor_lon);
    dist = Math.hypot(de, dn);
  }
  const hd = getHeading();
  // a heading-only calibration anchors nothing: report it as calibrated
  // direction + GPS position
  const anchored = !!cal && !!cal.pos_anchor;
  // a lid calibration anchors the position; say where the direction comes from
  const direction = cal && cal.method === "lid" ? cal.heading_from ?? (cal.mode === "full" ? cal.short ?? "the lid" : "compass") : null;
  const st = anchorStatus({ calibrated: anchored, label: cal?.short ?? cal?.label ?? null, sigma: fix.accuracy,
    anchorDistM: dist, held: holdingPosition(), compassNote: hd.disturbed ? hd.note ?? "Compass disturbed: move away from cars / metal" : null,
    direction });
  if (cal && !anchored && st.level === "gps") st.text = `Direction calibrated; position is GPS + motion (±${fix.accuracy.toFixed(1)} m)`;
  return st;
}

/** "Sensor details" (2026-10-05): what the heading is made of, so a field
 * user can report what they see. Refreshed from draw() at most 4x a second. */
function renderSensors(force = false): void {
  const el = A.sensors;
  if (!el || !A.sensorsOn) return;
  if (!force && Date.now() - A.sensorsAt < 250) return;
  A.sensorsAt = Date.now();
  const hd = getHeading();
  const d = hd.details;
  const f1 = (v: number | null | undefined) => (v === null || v === undefined || !Number.isFinite(v) ? "–" : v.toFixed(1));
  const cal = A.cal;
  const refWords: Record<string, string> = { none: "none yet", provisional: "provisional (not yet seen in a reliable pose)", locked: "locked", absolute: "absolute (Android)" };
  const axis = d?.compassAxis === "top" ? "top edge" : d?.compassAxis === "camera" ? "camera" : "learning";
  fill(el,
    h("div", {}, `Heading ${f1(hd.heading)}° · ${d?.source ?? hd.source}${hd.disturbed ? " · DISTURBED" : ""}`),
    h("div", {}, `Compass alone ${f1(d?.compass ?? hd.raw)}°${d?.accuracy != null ? ` ±${Math.round(d.accuracy)}°` : ""} · ref ${refWords[d?.ref ?? "none"] ?? d?.ref}${d?.source?.startsWith("iOS") ? ` · axis: ${axis}` : ""}`),
    h("div", {}, !d ? "–" : d.used ? `Compass in use${d.why ? ` (${d.why})` : ""}`
      : `Compass not used: ${d.why ?? "–"}${d.refAgeS !== null ? ` (last used ${Math.round(d.refAgeS)} s ago)` : ""}`),
    h("div", {}, `Camera pitch ${f1(d?.pitch)}° · roll ${f1(d?.roll)}° · β ${f1(hd.pitch === null ? null : hd.pitch + 90)} γ ${f1(hd.roll)} · turns: ${d?.yaw ?? "–"}`),
    h("div", {}, cal ? `Calibration: ${cal.mode === "position" ? "position only" : cal.method ?? "?"}, direction ${signed(cal.heading_offset_deg, 1)}° from ${cal.heading_from ?? cal.label ?? "?"}` : "Calibration: none"),
    voDetails());
}

function voDetails(): HTMLElement | null {
  const st = A.voOn ? A.vo?.voStatus() : null;
  if (!st || !st.on) return null;
  return h("div", {}, `Camera tracking ${st.quality} · ${st.inliers} ground points · ${st.fps.toFixed(0)} fps · ${st.ms.toFixed(0)} ms/frame · camera vs gyro turn ${st.yawErr.toFixed(1)}°`);
}

function renderLegend(): void {
  // the legend lives in the toolbar's popover now (2026-10-06); the old panel stays hidden
  if (A.legend) A.legend.hidden = true;
  if (A.popKind === "legend") renderPop();
}

/** Every mode change goes through here. The overlays of the mode being
 * left are cleared unconditionally - the sheet is closed AND emptied, the
 * preview dropped, the crosshair / road guides stop being drawn (they are
 * drawn per frame from overlaysFor(mode); lastPoseKey="" forces that frame
 * now) - and a mode that needs the sheet fills it after this returns. */
function setMode(action: ArAction): void {
  A.mode = nextMode(A.mode, action);
  const o = overlaysFor(A.mode);
  A.menuOpen = false;
  A.popKind = null;
  renderPop();
  closeSheet();
  if (!o.previewCal) A.preview = null;
  A.lastPoseKey = "";
  renderPanel();
  renderCalBar(true);
  setInfo();
}

function openSheet(...children: (HTMLElement | null)[]): void {
  if (!A.sheet) return;
  // a mode that never shows the sheet (tapping, road guides) never gets one
  if (overlaysFor(A.mode).sheet === "never") return;
  A.statusOpen = false;
  if (A.popKind) {
    A.popKind = null;
    renderPop();
    renderPanel();
  }
  fill(A.sheet, ...children);
  A.sheet.hidden = false;
}

function closeSheet(): void {
  A.statusOpen = false;
  if (A.sheet) {
    A.sheet.hidden = true;
    fill(A.sheet);
  }
}

function cycleFps(): void {
  A.fps = A.fps <= 5 ? 12 : A.fps >= 24 ? 5 : 24;
  lsSet(FPS_KEY, String(A.fps));
  renderPanel();
}

function setFov(v: number): void {
  A.fov = Math.max(40, Math.min(90, v));
  lsSet(FOV_KEY, String(A.fov));
  A.lastPoseKey = "";
  if (A.popKind === "more") renderPop();
}

/** The instruction line under the safety line. Since 2026-10-06 it is
 * shown only when there is something to do or wait for (calibration steps,
 * "waiting for GPS", the first-session "hold and drag" hint); accuracy and
 * counts moved to the status pill and its sheet (the field header used to
 * repeat them too). `extra`: a message that must be shown now. */
function setInfo(extra?: string): void {
  if (!A.info) return;
  const fix = A.host?.getFix();
  const bits: string[] = [];
  if (A.mode === "cal-tap") bits.push("Tap the middle of a meter cover, valve lid, hydrant or manhole you can see");
  if (A.mode === "cal-pick") bits.push("Which one did you tap? (or tap again)");
  if (A.mode === "cal-preview") {
    bits.push(A.grab ? "Drag it onto where it really is · let go to check"
      : A.twist ? "Twist to turn the overlay · let go to check"
        : A.preview?.direct ? "Drag another thing onto its real spot, twist to turn, or Apply" : "Preview: the overlay as it would be after this - Apply or Cancel");
  }
  if (A.mode === "road") bits.push("Point the camera ALONG the street from the footpath, road and kerb in the lower half, horizon in view - hold still");
  if (A.mode === "road-manual") bits.push("Stand on the footpath and turn until the kerb points at the yellow dot on the horizon (along one of the yellow lines), then tap 'Lined up'");
  if (A.mode === "kerb") {
    bits.push(A.kerbTaps.length === 0
      ? "Kerb 1/2: tap the edge of the road (the kerb or channel) beside you, a few metres ahead"
      : "Kerb 2/2: now tap the SAME kerb further along (5-15 m ahead)");
  }
  if (extra) bits.unshift(extra);
  else if (!fix) bits.unshift("Waiting for GPS…");
  if (!bits.length && A.mode === "view" && Date.now() < A.coachUntil) bits.push("Overlay off? Hold a pipe or lid, then drag it onto where it really is.");
  const text = bits.join(" · ");
  if (A.info.textContent !== text) A.info.textContent = text;
  A.info.hidden = !text;
}

function calCompassErr(reported: number | null): number {
  const base = compassErrorDeg(reported ?? A.data?.uncertainty.compass_deg ?? null);
  // a calibration is only as good as the compass stays put: assume 5° after one
  const err = A.cal ? Math.max(5, A.data?.uncertainty.min_compass_deg ?? 3) : base;
  // magnetically disturbed (held on the gyro): be honest about it
  return getHeading().disturbed ? Math.max(err, 15) : err;
}

async function maybeFetch(fix: ArFix): Promise<void> {
  if (A.fetching) return;
  const d = A.data;
  if (d) {
    const [de, dn] = localOffset(d.origin.lat, d.origin.lon, fix.lat, fix.lon);
    if (Math.hypot(de, dn) < REFETCH_MOVE_M && Date.now() - A.fetchedAt < REFETCH_AGE_MS) return;
  }
  if (!navigator.onLine && d) return; // keep the last set offline
  A.fetching = true;
  try {
    const hd = getHeading();
    A.data = await fieldApi.arAssets({ lat: fix.lat, lon: fix.lon, accuracy: fix.accuracy, heading_accuracy: hd.accuracy });
    A.fetchedAt = Date.now();
    A.lastPoseKey = "";
  } catch {
    if (A.info && !A.data) A.info.textContent = "Couldn't load nearby assets (offline?). The radar works with the offline cache.";
  } finally {
    A.fetching = false;
  }
}

function loop(): void {
  if (!A.running) return;
  A.raf = requestAnimationFrame(loop);
  const now = performance.now();
  if (now - A.lastDraw < 1000 / A.fps) return;
  const fix = A.host?.getFix() ?? null;
  const hd = getHeading();
  if (hd.heading !== null && hd.pitch !== null) A.sensorSeenAt = Date.now();
  // no orientation at all 4 s after the camera opened: fall back to the radar
  if (!A.sensorSeenAt && Date.now() - A.startedAt > 4000) {
    const why = "No compass / tilt readings from this device - the camera view needs both.";
    showToast(`${why} Showing the radar.`);
    stopAr();
    A.host?.fallbackToRadar(why);
    return;
  }
  if (fix) void maybeFetch(fix);
  const key = `${hd.heading?.toFixed(1)}|${hd.pitch?.toFixed(1)}|${hd.roll?.toFixed(1)}|${fix?.lat.toFixed(6)}|${fix?.lon.toFixed(6)}|${A.fetchedAt}|${A.cal?.at ?? 0}|${A.preview?.cal.at ?? 0}|${A.mode}|${A.selected}|${A.fov}`;
  // redraw on change, or once a second (labels, expiry)
  if (key === A.lastPoseKey && now - A.lastDraw < 1000) return;
  A.lastPoseKey = key;
  A.lastDraw = now;
  draw(fix);
}

// ---------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------

function drawLimits(): { far: number; fade: number; bandMax: number } {
  const d = A.data?.draw;
  const h0 = A.data?.camera_height_m;
  return {
    far: d?.max_range_m ?? maxDrawRangeM(h0),
    fade: d?.fade_start_m ?? AR_FADE_START_M,
    bandMax: d?.band_max_m ?? BAND_MAX_M,
  };
}

function draw(fix: ArFix | null): void {
  const canvas = A.canvas, video = A.video;
  if (!canvas || !video) return;
  const W = canvas.clientWidth, H = canvas.clientHeight;
  const dpr = Math.min(window.devicePixelRatio || 1, 2); // cap: 3x retina canvases cost battery
  if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);
  A.hits = [];
  const hd = getHeading();
  renderSensors();
  if (!fix || hd.heading === null || hd.pitch === null || hd.roll === null) {
    setInfo(fix ? "Waiting for the compass…" : undefined);
    return;
  }
  const overlays = overlaysFor(A.mode);
  // previewing: draw as if the candidate calibration were applied
  const cal = overlays.previewCal && A.preview ? A.preview.cal : activeCal(fix);
  const beta = hd.pitch + 90, gamma = hd.roll;
  const sa = screenAngle();
  const rawBasis = cameraBasis(hd.heading, beta, gamma, sa);
  const basis = cameraBasis(hd.heading + (cal?.heading_offset_deg ?? 0), beta, gamma, sa);
  if (!basis || !rawBasis) {
    setInfo("Hold the phone upright, camera facing ahead");
    return;
  }
  // cover-fit: the video is cropped to fill the view, the focal length scales with it
  const vw = video.videoWidth || W, vh = video.videoHeight || H;
  const s = Math.max(W / vw, H / vh);
  const focal = focalPx(Math.max(vw, vh), A.fov) * s;
  A.view = { basis, focal, width: W, height: H };
  A.rawView = { basis: rawBasis, focal, width: W, height: H };
  // drags and twists are measured through the calibration in FORCE, not
  // the preview being built (2026-10-06 direct manipulation)
  const baseCal = A.preview?.direct ? A.preview.direct.base : currentCalibration(fix);
  const baseBasis = cameraBasis(hd.heading + (baseCal?.heading_offset_deg ?? 0), beta, gamma, sa);
  A.baseView = baseBasis ? { basis: baseBasis, focal, width: W, height: H } : null;
  const v = A.view;
  if (overlays.roadGuides) drawRoadGuides(ctx, v);
  renderCalBar();
  const data = A.data;
  if (!data) {
    setInfo("Loading nearby assets…");
    return;
  }
  const [oe, on] = localOffset(data.origin.lat, data.origin.lon, fix.lat, fix.lon);
  // An APPLIED calibration's position part lives in the tracker (the fix is
  // already the anchored, filtered position); only a preview - or a
  // calibration from before 2026-10-05 restored from the session - is
  // still a fixed east/north offset on top.
  const shift = cal && !cal.anchored;
  A.rawDevE = oe;
  A.rawDevN = on;
  A.devE = oe + (shift ? cal.east_m : 0);
  A.devN = on + (shift ? cal.north_m : 0);
  const baseShift = !!baseCal && !baseCal.anchored;
  A.baseDevE = oe + (baseShift ? baseCal.east_m : 0);
  A.baseDevN = on + (baseShift ? baseCal.north_m : 0);
  const { far, fade, bandMax } = drawLimits();
  if (overlays.previewCal && A.preview?.guide) drawGuideLines(ctx, v, A.preview.guide, data.origin);
  if (overlays.previewCal && A.preview?.road && A.rawView) drawRoadLines(ctx, A.rawView, A.preview.road);
  if (overlays.kerbTaps) drawKerbTaps(ctx, v);
  const posCal = !!cal && !cal.anchored && cal.method !== "road" && cal.method !== "camera" && Math.abs(cal.range_shift_m) > 0;
  const gpsTerm = effectiveGpsM(fix.accuracy, posCal);
  const compassErr = calCompassErr(hd.accuracy);
  let drawn = 0, beyond = 0;
  const labels: { x: number; y: number; a: ArAsset; d: number; alpha: number }[] = [];
  const items = [...data.assets].sort((p, q) => q.distance_m - p.distance_m); // far to near
  // pipes first (under the pins)
  for (const a of items) {
    if (a.kind !== "line" || !a.path_enu) continue;
    const path = a.path_enu.map(([e, n]) => [e - A.devE, n - A.devN] as [number, number]);
    const runs = clipPathToDisc(path, far);
    if (!runs.length) {
      beyond++;
      continue;
    }
    const colour = assetColour(a);
    const sel = a.asset_id === A.selected;
    const hitRuns: [number, number][][] = [];
    for (const run of runs) {
      const pts = subdivide(run, 2);
      if (sel) drawCorridor(ctx, v, pts, lateralUncertaintyM(Math.max(nearestDist(pts), 1), gpsTerm, compassErr), colour, bandMax);
      for (let i = 0; i + 1 < pts.length; i++) {
        const mid = Math.hypot((pts[i][0] + pts[i + 1][0]) / 2, (pts[i][1] + pts[i + 1][1]) / 2);
        const alpha = groundFade(mid, far, fade);
        if (alpha <= 0.02) continue;
        for (const sr of projectPolyline([pts[i], pts[i + 1]], v)) {
          const w = Math.max(2.5, Math.min(sel ? 10 : 7, 60 / Math.max(mid, 1)));
          ctx.lineCap = "round";
          ctx.strokeStyle = `rgba(0,0,0,${0.45 * alpha})`;
          ctx.lineWidth = w + 2.5;
          strokeRun(ctx, sr);
          ctx.strokeStyle = hexA(colour, (sel ? 1 : 0.9) * alpha);
          ctx.lineWidth = w;
          strokeRun(ctx, sr);
          hitRuns.push(sr);
        }
      }
    }
    if (hitRuns.length) {
      drawn++;
      const q = nearestOnPath(path);
      const dq = Math.hypot(q[0], q[1]);
      A.hits.push({ a, runs: hitRuns, d: dq });
      const p = projectGroundPoint(q[0], q[1], v, far);
      if (p) labels.push({ x: p[0], y: p[1], a, d: dq, alpha: groundFade(dq, far, fade) });
    }
  }
  for (const a of items) {
    if (a.kind !== "point" || a.e === undefined || a.n === undefined) continue;
    const e = a.e - A.devE, n = a.n - A.devN;
    const d = Math.max(Math.hypot(e, n), 0.5);
    const p = projectGroundPoint(e, n, v, far);
    if (!p) {
      if (d > far) beyond++;
      continue;
    }
    if (p[0] < -50 || p[0] > W + 50 || p[1] < -50 || p[1] > H + 80) continue;
    drawn++;
    const alpha = groundFade(d, far, fade);
    const colour = assetColour(a);
    const sel = a.asset_id === A.selected;
    if (sel) drawGroundCircle(ctx, v, e, n, lateralUncertaintyM(d, gpsTerm, compassErr), colour, bandMax);
    const r = Math.max(7, Math.min(13, 90 / d)) * (sel ? 1.25 : 1);
    const top = p[1] - Math.max(18, Math.min(56, 320 / d));
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "rgba(255,255,255,0.9)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(p[0], p[1]);
    ctx.lineTo(p[0], top);
    ctx.stroke();
    ctx.fillStyle = hexA(colour, 0.9);
    ctx.beginPath();
    ctx.ellipse(p[0], p[1], 4, 2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    drawSymbol(ctx, symbolOf(a.asset_type), p[0], top, r, colour, alpha);
    if (sel) {
      ctx.strokeStyle = "#fefcbf";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p[0], top, r + 4, 0, Math.PI * 2);
      ctx.stroke();
    }
    A.hits.push({ a, head: [p[0], top], ground: p, d });
    labels.push({ x: p[0], y: top - r - 3, a, d, alpha });
  }
  drawLabels(ctx, labels, W, H, labelBand(H));
  if (overlays.crosshair) drawCrosshair(ctx, W, H);
  drawDirectMarks(ctx);
  const notes: string[] = [];
  notes.push(drawn ? `${drawn} within ${Math.round(far)} m` : `nothing mapped within ${Math.round(far)} m in view`);
  if (beyond) notes.push(`${beyond} further away (see the radar)`);
  A.drawNote = `${notes.join(", ")}. Tap one to identify it.`;
  setInfo();
}

function nearestDist(pts: [number, number][]): number {
  const q = nearestOnPath(pts);
  return Math.hypot(q[0], q[1]);
}

/** The selected pipe's uncertainty corridor: two faint dashed ground lines
 * (true perspective) and a very light fill, clamped to bandMax either side. */
function drawCorridor(ctx: CanvasRenderingContext2D, v: View, pts: [number, number][], w: number, colour: string, bandMax: number): void {
  const [left, right] = corridorEdges(pts, w, bandMax);
  ctx.save();
  ctx.fillStyle = hexA(colour, 0.08);
  for (let i = 0; i + 1 < pts.length; i++) {
    const quad = [left[i], left[i + 1], right[i + 1], right[i]].map((q) => projectPoint(q[0], q[1], 0, v));
    if (quad.some((q) => q === null)) continue;
    ctx.beginPath();
    quad.forEach((q, k) => (k ? ctx.lineTo(q![0], q![1]) : ctx.moveTo(q![0], q![1])));
    ctx.closePath();
    ctx.fill();
  }
  ctx.setLineDash([6, 6]);
  ctx.strokeStyle = hexA(colour, 0.75);
  ctx.lineWidth = 1.5;
  for (const edge of [left, right]) for (const r of projectPolyline(edge, v)) strokeRun(ctx, r);
  ctx.restore();
}

function drawGroundCircle(ctx: CanvasRenderingContext2D, v: View, e: number, n: number, w: number, colour: string, bandMax: number): void {
  const r = Math.min(w, bandMax);
  const ring: [number, number][] = [];
  for (let k = 0; k <= 32; k++) {
    const a = (k / 32) * Math.PI * 2;
    ring.push([e + r * Math.sin(a), n + r * Math.cos(a)]);
  }
  ctx.save();
  ctx.setLineDash([6, 6]);
  ctx.strokeStyle = hexA(colour, 0.75);
  ctx.fillStyle = hexA(colour, 0.08);
  ctx.lineWidth = 1.5;
  for (const run of projectPolyline(ring, v)) {
    ctx.beginPath();
    run.forEach((q, k) => (k ? ctx.lineTo(q[0], q[1]) : ctx.moveTo(q[0], q[1])));
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

/** The band of the canvas that labels may use: below the top bar (and the
 * instruction line when shown), above the bottom stack (toolbar, sheet,
 * menus), so a label never hides under them (field test 2026-10-06:
 * "Wastewater lateral · 5 m" behind the legend). */
function labelBand(H: number): [number, number] {
  const c = A.canvas?.getBoundingClientRect();
  if (!c || !A.root) return [40, H - 80];
  let top = 28, bottom = H - 8;
  for (const el of A.root.querySelectorAll<HTMLElement>(".fa-ar-topbar, .fa-ar-top > :not([hidden])")) {
    const r = el.getBoundingClientRect();
    if (r.height) top = Math.max(top, r.bottom - c.top);
  }
  for (const el of A.root.querySelectorAll<HTMLElement>(".fa-ar-bottom > :not([hidden])")) {
    const r = el.getBoundingClientRect();
    if (r.height) bottom = Math.min(bottom, r.top - c.top);
  }
  return [top + 22, Math.max(top + 60, bottom - 4)];
}

function drawLabels(ctx: CanvasRenderingContext2D, labels: { x: number; y: number; a: ArAsset; d: number; alpha: number }[], W: number, _H: number,
  band: [number, number]): void {
  // the selected asset always; otherwise the nearest 4, short ("Water main · 6 m")
  labels.sort((p, q) => (q.a.asset_id === A.selected ? 1 : 0) - (p.a.asset_id === A.selected ? 1 : 0) || p.d - q.d);
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  const placed: [number, number, number, number][] = [];
  let n = 0;
  for (const l of labels) {
    const sel = l.a.asset_id === A.selected;
    // the nearest three only (they overlapped on the phone with four and long names)
    if (!sel && n >= 3) continue;
    const txt = sel
      ? `${l.a.type_label} · ${NETWORK_LABELS[l.a.network as keyof typeof NETWORK_LABELS] ?? ""} · ${Math.round(l.d)} m${l.a.depth_label ? ` · ${l.a.depth_label}` : ""}`
      : `${l.a.type_label} · ${Math.round(l.d)} m`;
    ctx.font = `${sel ? 700 : 600} ${sel ? 14 : 12}px system-ui, sans-serif`;
    const tw = ctx.measureText(txt).width;
    const x = Math.max(tw / 2 + 4, Math.min(W - tw / 2 - 4, l.x));
    if (l.y < band[0] - 40 || l.y > band[1] + 40) continue; // far outside the free band: not worth a label
    const y = Math.max(band[0], Math.min(band[1], l.y));
    const box: [number, number, number, number] = [x - tw / 2 - 4, y - 18, tw + 8, 20];
    if (!sel && placed.some((b) => box[0] < b[0] + b[2] && b[0] < box[0] + box[2] && box[1] < b[1] + b[3] && b[1] < box[1] + box[3])) continue;
    placed.push(box);
    n++;
    ctx.globalAlpha = Math.max(0.35, l.alpha);
    ctx.fillStyle = "rgba(15,26,36,0.78)";
    ctx.fillRect(box[0], box[1], box[2], box[3]);
    ctx.fillStyle = hexA(assetColour(l.a), 1);
    ctx.fillRect(box[0], box[1], 3, box[3]);
    ctx.fillStyle = "#fff";
    ctx.fillText(txt, x, y);
    ctx.globalAlpha = 1;
  }
}

function drawCrosshair(ctx: CanvasRenderingContext2D, W: number, H: number): void {
  ctx.strokeStyle = "#f6e05e";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(W / 2, H / 2, 18, 0, Math.PI * 2);
  ctx.moveTo(W / 2 - 28, H / 2);
  ctx.lineTo(W / 2 + 28, H / 2);
  ctx.moveTo(W / 2, H / 2 - 28);
  ctx.lineTo(W / 2, H / 2 + 28);
  ctx.stroke();
}

/** "Line up with the road" guides (reworked 2026-10-05). The old guides
 * were two ground lines 3.5 m either side of the CAMERA - the kerbs of a
 * road you stand in the middle of. On the footpath the near kerb is 1-4 m
 * to one side and the far one 8-14 m to the other, so they never matched
 * and "lined up" was guesswork. Any line on the ground parallel to the view
 * direction, at ANY sideways offset, runs to the same vanishing point on
 * the horizon straight ahead. So: a yellow dot there, a short horizon tick,
 * and a fan of faint ground lines at a range of sideways offsets - turn
 * until the kerb points at the dot (it then lies along one of the lines). */
function drawRoadGuides(ctx: CanvasRenderingContext2D, v: View): void {
  const hdg = (v.basis.heading * Math.PI) / 180;
  const f: [number, number] = [Math.sin(hdg), Math.cos(hdg)];
  const r: [number, number] = [Math.cos(hdg), -Math.sin(hdg)];
  ctx.save();
  ctx.lineCap = "round";
  for (const off of [-10, -8, -6, -4.5, -3, -1.5, 1.5, 3, 4.5, 6, 8, 10]) {
    const line: [number, number][] = [];
    for (let t = 1.5; t <= 400; t *= 1.25) line.push([off * r[0] + t * f[0], off * r[1] + t * f[1]]);
    const near = Math.abs(off) <= 4.5;
    ctx.strokeStyle = `rgba(246,224,94,${near ? 0.85 : 0.5})`;
    ctx.lineWidth = near ? 2.5 : 1.5;
    for (const run of projectPolyline(line, v)) strokeRun(ctx, run);
  }
  // the vanishing point (eye level, far ahead) and a horizon tick
  const vp = projectPoint(f[0] * 1000, f[1] * 1000, CAMERA_HEIGHT_M, v);
  if (vp) {
    const a = projectPoint(f[0] * 1000 - r[0] * 120, f[1] * 1000 - r[1] * 120, CAMERA_HEIGHT_M, v);
    const b = projectPoint(f[0] * 1000 + r[0] * 120, f[1] * 1000 + r[1] * 120, CAMERA_HEIGHT_M, v);
    if (a && b) {
      ctx.strokeStyle = "rgba(246,224,94,0.7)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 6]);
      strokeRun(ctx, [a, b]);
      ctx.setLineDash([]);
    }
    ctx.fillStyle = "#f6e05e";
    ctx.strokeStyle = "rgba(0,0,0,0.6)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(vp[0], vp[1], 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    const W = v.width, H = v.height;
    if (vp[1] < 0 || vp[1] > H || vp[0] < 0 || vp[0] > W) {
      ctx.fillStyle = "#f6e05e";
      ctx.font = "600 13px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(vp[1] < 0 ? "Tilt the phone up a little: the yellow dot is the horizon" : "Tilt the phone down a little", W / 2, 70);
    }
  }
  ctx.restore();
}

/** The calibration preview's kerb lines (LINZ centreline +/- half-width):
 * after a good calibration they lie on the real kerbs. `g.lines` are ENU
 * metres from (g.lat, g.lon). */
function drawGuideLines(ctx: CanvasRenderingContext2D, v: View, g: NonNullable<Preview["guide"]>, origin: { lat: number; lon: number }): void {
  const [ge, gn] = localOffset(origin.lat, origin.lon, g.lat, g.lon);
  ctx.save();
  ctx.setLineDash([12, 8]);
  ctx.lineCap = "round";
  for (const ln of g.lines) {
    const path = ln.map(([e, n]) => [ge + e - A.devE, gn + n - A.devN] as [number, number]);
    for (const run of projectPolyline(subdivide(path, 2), v)) {
      ctx.strokeStyle = "rgba(0,0,0,0.5)";
      ctx.lineWidth = 6;
      strokeRun(ctx, run);
      ctx.strokeStyle = "#f6e05e";
      ctx.lineWidth = 3;
      strokeRun(ctx, run);
    }
  }
  ctx.restore();
}

/** The lines the camera found, drawn where they were seen: in the RAW pose
 * (the compass as it was when the frame was taken), so they stay on the
 * kerb in the live picture. The one in use is thick yellow, numbered, the
 * others thin white - tap one to use it instead. Returns their screen runs
 * for hit testing. */
function roadLineRuns(v: View, rp: RoadPick): { id: number; runs: [number, number][][] }[] {
  return rp.res.candidates.map((c) => {
    const path = c.ground_enu_raw.map(([e, n]) => [rp.capE + e - A.rawDevE, rp.capN + n - A.rawDevN] as [number, number]);
    return { id: c.id, runs: projectPolyline(subdivide(path, 1), v) };
  });
}

function drawRoadLines(ctx: CanvasRenderingContext2D, v: View, rp: RoadPick): void {
  ctx.save();
  ctx.lineCap = "round";
  for (const { id, runs } of roadLineRuns(v, rp)) {
    const sel = id === rp.selected;
    for (const run of runs) {
      ctx.setLineDash(sel ? [] : [8, 6]);
      ctx.strokeStyle = "rgba(0,0,0,0.55)";
      ctx.lineWidth = sel ? 8 : 5;
      strokeRun(ctx, run);
      ctx.strokeStyle = sel ? "#f6e05e" : "rgba(255,255,255,0.85)";
      ctx.lineWidth = sel ? 5 : 2.5;
      strokeRun(ctx, run);
    }
    const last = runs.length ? runs[runs.length - 1] : null;
    const tip = last ? last[last.length - 1] : null;
    if (tip) {
      ctx.setLineDash([]);
      ctx.fillStyle = sel ? "#f6e05e" : "#fff";
      ctx.beginPath();
      ctx.arc(tip[0], tip[1], 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#0f1a24";
      ctx.font = "bold 11px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(id + 1), tip[0], tip[1] + 0.5);
    }
  }
  ctx.restore();
}

/** The kerb taps so far (yellow ground markers) and the line through two. */
function drawKerbTaps(ctx: CanvasRenderingContext2D, v: View): void {
  const pts = A.kerbTaps.map((k) => [k.draw[0] - A.devE, k.draw[1] - A.devN] as [number, number]);
  ctx.save();
  if (pts.length === 2) {
    const [p, q] = pts;
    const dx = q[0] - p[0], dy = q[1] - p[1];
    const L = Math.hypot(dx, dy) || 1;
    const line = subdivide([[p[0] - (dx / L) * 6, p[1] - (dy / L) * 6], [q[0] + (dx / L) * 10, q[1] + (dy / L) * 10]], 1);
    ctx.strokeStyle = "#f6e05e";
    ctx.lineWidth = 3;
    for (const run of projectPolyline(line, v)) strokeRun(ctx, run);
  }
  pts.forEach((p, i) => {
    const s = projectPoint(p[0], p[1], 0, v);
    if (!s) return;
    ctx.fillStyle = "#f6e05e";
    ctx.strokeStyle = "rgba(0,0,0,0.7)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(s[0], s[1], 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#0f1a24";
    ctx.font = "bold 11px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), s[0], s[1] + 0.5);
  });
  ctx.restore();
}

function strokeRun(ctx: CanvasRenderingContext2D, run: [number, number][]): void {
  ctx.beginPath();
  ctx.moveTo(run[0][0], run[0][1]);
  for (let i = 1; i < run.length; i++) ctx.lineTo(run[i][0], run[i][1]);
  ctx.stroke();
}

// ---------------------------------------------------------------------------
// Taps: identify, or calibrate
// ---------------------------------------------------------------------------

function segDist(px: number, py: number, a: [number, number], b: [number, number]): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const L = dx * dx + dy * dy;
  const t = L === 0 ? 0 : Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / L));
  return Math.hypot(px - (a[0] + t * dx), py - (a[1] + t * dy));
}

/** The drawn asset under a screen point (points win over lines; nearer wins). */
export function hitTest(hits: Hit[], x: number, y: number): Hit | null {
  let best: Hit | null = null, bestD = Infinity;
  for (const hh of hits) {
    if (!hh.head || !hh.ground) continue;
    const d = Math.min(Math.hypot(x - hh.head[0], y - hh.head[1]), Math.hypot(x - hh.ground[0], y - hh.ground[1]) + 6);
    if (d <= TAP_POINT_PX && d < bestD) {
      best = hh;
      bestD = d;
    }
  }
  if (best) return best;
  for (const hh of hits) {
    for (const run of hh.runs ?? []) {
      for (let i = 0; i + 1 < run.length; i++) {
        const d = segDist(x, y, run[i], run[i + 1]);
        if (d <= TAP_LINE_PX && d < bestD) {
          best = hh;
          bestD = d;
        }
      }
    }
  }
  return best;
}

// --- gestures (2026-10-06) ---------------------------------------------------
//
// The canvas takes pointer events (touch-action: none) through
// arGesture.GestureClassifier: a quick tap identifies (as before), a hold
// (HOLD_MS, a haptic tick on Android, a ring on every phone - iOS Safari has
// no vibrate) then a drag moves the whole overlay, a two-finger twist turns
// it. The pinch distance is ignored: nothing scales.

function bindGestures(canvas: HTMLCanvasElement): void {
  const xy = (e: PointerEvent): [number, number] => {
    const r = canvas.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  canvas.addEventListener("pointerdown", (e) => {
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      /* not capturable (synthetic) */
    }
    const [x, y] = xy(e);
    onGestures(A.gest.down(e.pointerId, x, y, performance.now()));
    if (A.gestTimer !== null) window.clearTimeout(A.gestTimer);
    A.gestTimer = window.setTimeout(() => {
      A.gestTimer = null;
      onGestures(A.gest.tick(performance.now()));
    }, A.gest.holdMs + 15);
  });
  canvas.addEventListener("pointermove", (e) => {
    const [x, y] = xy(e);
    onGestures(A.gest.move(e.pointerId, x, y, performance.now()));
  });
  const up = (e: PointerEvent) => {
    const [x, y] = xy(e);
    onGestures(A.gest.up(e.pointerId, x, y, performance.now()));
  };
  canvas.addEventListener("pointerup", up);
  canvas.addEventListener("pointercancel", () => onGestures(A.gest.cancelAll()));
  canvas.addEventListener("contextmenu", (e) => e.preventDefault());
}

/** a direct-manipulation calibration may start or continue in this mode */
function directAllowed(): boolean {
  return A.mode === "view" || (A.mode === "cal-preview" && !!A.preview?.direct);
}

function onGestures(evs: GestureEvent[]): void {
  for (const e of evs) {
    switch (e.type) {
      case "tap":
        onTap(e.x, e.y);
        break;
      case "hold":
        if (directAllowed()) startGrab(e.x, e.y);
        break;
      case "drag":
        if (A.grab) moveGrab(e.x, e.y);
        break;
      case "drag-end":
        if (A.grab) endGrab(e.x, e.y);
        break;
      case "rotate-start":
        if (directAllowed()) startTwist(e.cx, e.cy);
        break;
      case "rotate":
        if (A.twist) updateTwist(e.deg);
        break;
      case "rotate-end":
        if (A.twist) endTwist(e.deg);
        break;
      case "cancel":
        cancelLive();
        break;
    }
  }
}

function haptic(): void {
  try {
    (navigator as Navigator & { vibrate?: (ms: number) => boolean }).vibrate?.(12);
  } catch {
    /* not allowed */
  }
}

function newDirect(): DirectState {
  const fix = A.host?.getFix() ?? null;
  return { base: currentCalibration(fix), pins: [], rot: 0, t: [0, 0], twisted: false, twistPairs: null, mode: "drag",
    residual: null, span: null, sanity: null, checked: null, serverNote: null };
}

function cloneDirect(d: DirectState): DirectState {
  return { ...d, pins: d.pins.map((p) => ({ ...p })), t: [d.t[0], d.t[1]], twistPairs: d.twistPairs ? d.twistPairs.map((x) => ({ p: [...x.p], q: [...x.q] }) as Pair) : null };
}

/** Start a hold-and-drag at (x, y): what is under the finger (a pin's
 * ground point, a pipe, or just the ground) becomes the point that follows
 * the finger. */
function startGrab(x: number, y: number): void {
  const bv = A.baseView, data = A.data;
  if (!bv || !data || !A.view) return;
  const prev = A.preview?.direct ? cloneDirect(A.preview.direct) : null;
  const ds = prev ? cloneDirect(prev) : newDirect();
  const hit = hitTest(A.hits, x, y);
  let anchor: [number, number] = [x, y];
  let P: [number, number] | null = null;
  if (hit && hit.ground && hit.a.e !== undefined && hit.a.n !== undefined) {
    // a lid / meter / hydrant: its ground point follows the finger, exactly
    anchor = [hit.ground[0], hit.ground[1]];
    P = [hit.a.e, hit.a.n];
  } else {
    // a pipe, or the ground: the map point drawn under the finger
    const q0 = groundPointAt(x, y, bv);
    if (q0) {
      const r = rotateEN(q0, ds.rot);
      P = [A.baseDevE + r[0] + ds.t[0], A.baseDevN + r[1] + ds.t[1]];
    }
  }
  if (!P) {
    showToast("Hold on the ground below the horizon, within about 30 m, to drag the overlay.");
    return;
  }
  haptic();
  A.holdRing = { x, y, at: performance.now() };
  A.grab = { s0: [x, y], anchor, P, short: hit ? hit.a.type_label.toLowerCase() : "the overlay", prev };
  if (A.mode === "view") {
    closePop();
    closeSheet();
    if (A.selected) select(null);
  }
  A.mode = nextMode(A.mode, "direct");
  A.preview = directPreview(ds);
  closeSheet();
  renderPanel();
  setInfo();
  A.lastPoseKey = "";
}

/** where the dragged point is now (data-origin ENU, base frame), or null off the ground */
function grabTarget(x: number, y: number): [number, number] | null {
  const g = A.grab, bv = A.baseView;
  if (!g || !bv) return null;
  const q = groundPointAt(g.anchor[0] + x - g.s0[0], g.anchor[1] + y - g.s0[1], bv);
  return q ? [A.baseDevE + q[0], A.baseDevN + q[1]] : null;
}

function withPin(pins: Pin[], pin: Pin): Pin[] {
  const kept = pins.filter((p) => Math.hypot(p.P[0] - pin.P[0], p.P[1] - pin.P[1]) >= PIN_REPLACE_M);
  return [...kept, pin].slice(-2);
}

function moveGrab(x: number, y: number): void {
  const g = A.grab;
  const Q = grabTarget(x, y);
  if (!g || !Q || !A.preview?.direct) return;
  const ds = cloneDirect(g.prev ?? newDirect());
  ds.base = A.preview.direct.base;
  ds.pins = withPin(ds.pins, { P: g.P, Q, short: g.short });
  solveDirect(ds);
  A.preview = directPreview(ds);
  A.lastPoseKey = "";
  showBadge(`${Math.hypot(ds.t[0], ds.t[1]).toFixed(1)} m${Math.abs(ds.rot) >= 0.5 ? ` · ${signed(ds.rot)}°` : ""}`);
}

function endGrab(x: number, y: number): void {
  moveGrab(x, y);
  const g = A.grab;
  A.grab = null;
  hideBadge();
  if (!g || !A.preview?.direct || !A.preview.direct.pins.some((p) => p.P === g.P)) {
    restoreDirect(g?.prev ?? null);
    return;
  }
  commitDirect(A.preview.direct);
}

/** A two-finger twist: about the last dragged point if there is one
 * (pin it, then twist), else about the ground under the fingers. */
function startTwist(cx: number, cy: number): void {
  const bv = A.baseView;
  if (!bv || !A.data) return;
  const prev = A.preview?.direct ? cloneDirect(A.preview.direct) : null;
  const ds = prev ? cloneDirect(prev) : newDirect();
  let pivot: [number, number] | null = ds.pins.length ? ds.pins[ds.pins.length - 1].Q : null;
  if (!pivot) {
    const q = groundPointAt(cx, cy, bv) ?? groundPointAt(bv.width / 2, bv.height * 0.75, bv);
    if (!q) {
      showToast("Point the camera at the ground to turn the overlay.");
      return;
    }
    pivot = [A.baseDevE + q[0], A.baseDevN + q[1]];
  }
  A.grab = null;
  A.twist = { pivot, rot0: ds.rot, t0: [ds.t[0], ds.t[1]], prev, pairs: null };
  if (A.mode === "view") {
    closePop();
    closeSheet();
  }
  A.mode = nextMode(A.mode, "direct");
  A.preview = directPreview(ds);
  closeSheet();
  renderPanel();
  setInfo();
  showBadge("↻ 0°");
}

function updateTwist(deg: number): void {
  const tw = A.twist;
  if (!tw || !A.preview?.direct) return;
  const ds = cloneDirect(tw.prev ?? newDirect());
  ds.base = A.preview.direct.base;
  const pairs = twistPairs([tw.pivot[0] - A.baseDevE, tw.pivot[1] - A.baseDevN], deg, tw.rot0, tw.t0);
  const sol = solveRigid2D(pairs);
  ds.rot = sol.rot_deg;
  ds.t = sol.t;
  ds.twisted = true;
  ds.twistPairs = pairs;
  ds.pins = ds.pins.slice(-1); // the pivot pin, if any, stays exactly where it is
  ds.mode = "rotate";
  ds.residual = ds.span = null;
  tw.pairs = pairs;
  ds.sanity = directSanity(Math.hypot(ds.t[0], ds.t[1]), ds.rot, null, null, gpsAcc());
  A.preview = directPreview(ds);
  A.lastPoseKey = "";
  showBadge(`${deg >= 0 ? "↻" : "↺"} ${Math.abs(deg).toFixed(0)}° · compass ${signed(ds.rot)}°`);
}

function endTwist(deg: number): void {
  updateTwist(deg);
  A.twist = null;
  hideBadge();
  if (A.preview?.direct) commitDirect(A.preview.direct);
}

/** The finger left without a usable gesture, or a second finger cut a drag
 * short: back to where things were before it. */
function cancelLive(): void {
  if (!A.grab && !A.twist) return;
  const prev = A.grab?.prev ?? A.twist?.prev ?? null;
  A.grab = null;
  A.twist = null;
  hideBadge();
  restoreDirect(prev);
}

/** Back to the direct preview as it was before the gesture, or to the
 * plain view when there was none. */
function restoreDirect(prev: DirectState | null): void {
  hideBadge();
  if (prev && (prev.pins.length || prev.twisted)) {
    A.preview = directPreview(prev);
    showDirectPreview();
  } else if (A.mode === "cal-preview" && A.preview?.direct) {
    setMode("cancel");
  }
  A.lastPoseKey = "";
}

function gpsAcc(): number | null {
  const fix = A.host?.getFix() ?? null;
  return fix ? fix.gpsAccuracy ?? fix.accuracy : null;
}

/** Solve (rot, t) from the pins (arMath.solveRigid2D): two pins far enough
 * apart fix the direction as well (two_point); one pin moves the position
 * and keeps the direction (a twist made earlier included). */
function solveDirect(ds: DirectState): void {
  const pairs = ds.pins.map((pin): Pair => ({ p: [pin.P[0] - A.baseDevE, pin.P[1] - A.baseDevN], q: [pin.Q[0] - A.baseDevE, pin.Q[1] - A.baseDevN] }));
  const two = pairs.length >= 2 ? solveRigid2D(pairs) : null;
  if (two && two.span_m >= TWO_POINT_MIN_SPAN_M) {
    ds.rot = two.rot_deg;
    ds.t = two.t;
    ds.mode = "two_point";
    ds.residual = two.residual_m;
    ds.span = two.span_m;
  } else if (pairs.length) {
    const sol = solveRigid2D(pairs.slice(-1), ds.rot);
    ds.t = sol.t;
    ds.mode = ds.twisted ? "rotate" : "drag";
    ds.residual = ds.span = null;
    if (pairs.length >= 2) ds.pins = ds.pins.slice(-1);
  }
  ds.sanity = directSanity(Math.hypot(ds.t[0], ds.t[1]), ds.rot, ds.residual, ds.span, gpsAcc());
}

/** The StoredCal a direct state previews: the base calibration's heading
 * plus rot, its (pre-anchor) offset plus t. */
function directPreview(ds: DirectState): Preview {
  const fix = A.host?.getFix() ?? null;
  const base = ds.base;
  const bE = base && !base.anchored ? base.east_m : 0, bN = base && !base.anchored ? base.north_m : 0;
  const last = ds.pins[ds.pins.length - 1] ?? null;
  const data = A.data;
  const anchorLL = last && data ? new LocalFrame(data.origin.lat, data.origin.lon).toLatLon(last.Q[0], last.Q[1]) : null;
  const turned = Math.abs(ds.rot) >= 0.05;
  const what = ds.mode === "two_point" ? `${ds.pins.map((p) => p.short).join(" and ")}` : last?.short ?? "the overlay";
  const dist = last ? Math.hypot(last.Q[0] - A.baseDevE, last.Q[1] - A.baseDevN) : 5;
  const sigma = ds.mode === "two_point" ? Math.max(0.5, ds.residual ?? 0, 0.05 * dist) : calibrationSigmaM(dist);
  const cal: StoredCal = {
    heading_offset_deg: (base?.heading_offset_deg ?? 0) + ds.rot,
    east_m: bE + ds.t[0], north_m: bN + ds.t[1], range_shift_m: Math.hypot(ds.t[0], ds.t[1]), accepted: true,
    at: Date.now(), lat: fix?.lat ?? 0, lon: fix?.lon ?? 0,
    ttl_s: A.data?.calibration.ttl_s ?? 900, max_move_m: A.data?.calibration.max_move_m ?? 40,
    method: "drag", direct: ds.mode, mode: turned ? "full" : "position",
    label: ds.mode === "two_point" ? `two points: ${what}` : ds.mode === "rotate" ? `turned by hand${last ? ` about the ${last.short}` : ""}` : `dragged: ${what}`,
    short: ds.mode === "two_point" ? "two points" : last?.short ?? undefined,
    anchor_lat: anchorLL ? anchorLL[0] : undefined, anchor_lon: anchorLL ? anchorLL[1] : undefined,
    sigma_m: sigma,
    heading_from: turned ? (ds.mode === "two_point" ? "two dragged points" : "a two-finger turn") : base?.heading_from ?? (base && Math.abs(base.heading_offset_deg) >= 0.05 ? base.label ?? "earlier calibration" : "compass"),
  };
  const sanity = ds.sanity ?? directSanity(Math.hypot(ds.t[0], ds.t[1]), ds.rot, ds.residual, ds.span, gpsAcc());
  return { cal, sanity, title: "", direct: ds };
}

/** The gesture ended: show the preview sheet and ask the server to re-solve. */
function commitDirect(ds: DirectState): void {
  solveDirectIfPins(ds);
  A.preview = directPreview(ds);
  showDirectPreview();
  void checkDirectOnServer(ds);
}

function solveDirectIfPins(ds: DirectState): void {
  if (ds.pins.length && !(ds.twisted && ds.pins.length === 1 && ds.mode === "rotate")) solveDirect(ds);
  else ds.sanity = directSanity(Math.hypot(ds.t[0], ds.t[1]), ds.rot, ds.residual, ds.span, gpsAcc());
}

/** POST /ar-calibrate with mode drag | rotate | two_point: the server
 * re-solves the same transform (field_ar.direct_calibration) and judges it.
 * Its answer wins; a disagreement with the phone's own maths is logged. */
async function checkDirectOnServer(ds: DirectState): Promise<void> {
  const fix = A.host?.getFix();
  if (!fix) return;
  const seq = ++A.directSeq;
  const rel = (pin: Pin) => ({ map_e: pin.P[0] - A.baseDevE, map_n: pin.P[1] - A.baseDevN, true_e: pin.Q[0] - A.baseDevE, true_n: pin.Q[1] - A.baseDevN });
  const body = ds.mode === "two_point"
    ? { mode: "two_point" as const, pairs: ds.pins.map(rel) }
    : ds.mode === "rotate" && ds.twistPairs && ds.pins.length <= 1 && !A.grab
      ? { mode: "rotate" as const, pairs: ds.twistPairs.map((x) => ({ map_e: x.p[0], map_n: x.p[1], true_e: x.q[0], true_n: x.q[1] })) }
      : { mode: "drag" as const, pairs: ds.pins.slice(-1).map(rel), rotation_deg: ds.rot };
  if (!body.pairs.length) return;
  try {
    const res = await fieldApi.arCalibrateDirect({ lat: fix.lat, lon: fix.lon, accuracy: fix.gpsAccuracy ?? fix.accuracy, ...body });
    if (seq !== A.directSeq || A.preview?.direct !== ds) return; // superseded by a newer gesture
    const dRot = Math.abs(res.heading_offset_deg - ds.rot), dT = Math.hypot(res.east_m - ds.t[0], res.north_m - ds.t[1]);
    if (res.accepted && (dRot > 0.2 || dT > 0.1)) console.warn("AR direct calibration: phone and server disagree", { phone: { rot: ds.rot, t: ds.t }, server: res });
    if (res.accepted) {
      ds.rot = res.heading_offset_deg;
      ds.t = [res.east_m, res.north_m];
    }
    ds.sanity = res.sanity ?? ds.sanity;
    if (!res.accepted && res.reason) ds.sanity = { level: "refuse", move_m: 0, heading_deg: 0, limit_m: 0, message: res.reason };
    ds.checked = "server";
  } catch {
    if (seq !== A.directSeq || A.preview?.direct !== ds) return;
    ds.checked = "phone";
    ds.serverNote = "Offline: checked on the phone only.";
  }
  A.preview = directPreview(ds);
  showDirectPreview();
}

function showDirectPreview(): void {
  const p = A.preview;
  const ds = p?.direct;
  if (!p || !ds) return;
  const t = Math.hypot(ds.t[0], ds.t[1]);
  // the overlay turns the opposite way to the heading correction
  p.title = ds.mode === "two_point" ? "Align on these two points?"
    : ds.mode === "rotate" ? `Turn the overlay ${Math.abs(ds.rot).toFixed(0)}° ${ds.rot < 0 ? "clockwise" : "anticlockwise"}?`
      : Math.abs(ds.rot) >= 0.05 ? `Move the overlay ${t.toFixed(1)} m (turn kept)?` : `Move the overlay ${t.toFixed(1)} m?`;
  showPreview(p);
}

/** The pins (where you put things: yellow rings A, B), the hold ring, and
 * nothing else - drawn in the BASE view, where the finger measured them. */
function drawDirectMarks(ctx: CanvasRenderingContext2D): void {
  const bv = A.baseView;
  const ds = A.preview?.direct;
  if (bv && ds && A.mode === "cal-preview") {
    ds.pins.forEach((pin, i) => {
      const s = projectPoint(pin.Q[0] - A.baseDevE, pin.Q[1] - A.baseDevN, 0, bv);
      if (!s) return;
      ctx.save();
      ctx.strokeStyle = "rgba(0,0,0,0.6)";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.arc(s[0], s[1], 13, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = "#f6e05e";
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.fillStyle = "#f6e05e";
      ctx.font = "bold 12px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String.fromCharCode(65 + i), s[0], s[1] - 24);
      ctx.restore();
    });
  }
  const ring = A.holdRing;
  if (ring) {
    const age = performance.now() - ring.at;
    if (age > 600) A.holdRing = null;
    else {
      ctx.save();
      ctx.strokeStyle = `rgba(246,224,94,${1 - age / 600})`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(ring.x, ring.y, 18 + age / 20, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      A.lastPoseKey = ""; // keep animating
    }
  }
}

function showBadge(text: string): void {
  if (!A.badge) return;
  A.badge.textContent = text;
  A.badge.hidden = false;
}

function hideBadge(): void {
  if (A.badge) A.badge.hidden = true;
}

function onTap(sx: number, sy: number): void {
  if (!A.canvas) return;
  if (A.popKind) {
    // a tap on the camera closes an open menu first
    closePop();
    return;
  }
  // tapping again while the list is up re-ranks for the new tap
  if (A.mode === "cal-tap" || A.mode === "cal-pick") {
    void calibrationTap(sx, sy);
    return;
  }
  if (A.mode === "kerb") {
    void kerbTap(sx, sy);
    return;
  }
  if (A.mode === "cal-preview" && A.preview?.road && A.rawView) {
    // a camera road calibration: tap another detected line to use it
    let best: { id: number; d: number } | null = null;
    for (const { id, runs } of roadLineRuns(A.rawView, A.preview.road)) {
      for (const run of runs) {
        for (let i = 0; i + 1 < run.length; i++) {
          const d = segDist(sx, sy, run[i], run[i + 1]);
          if (d <= 36 && (!best || d < best.d)) best = { id, d };
        }
      }
    }
    if (best && best.id !== A.preview.road.selected) {
      const c = A.preview.road.res.candidates.find((x) => x.id === best!.id);
      if (c) showRoadPreview({ ...A.preview.road, selected: c.id, role: c.role === "edge" ? roleForOther(c, A.preview.road.res) : c.role });
    }
    return;
  }
  if (A.mode !== "view") return; // road guides / kerb lines: taps do nothing
  A.menuOpen = false;
  renderPanel();
  const hit = hitTest(A.hits, sx, sy);
  if (!hit) {
    if (A.selected) select(null);
    closeSheet();
    return;
  }
  select(hit.a.asset_id);
  showIdentify(hit.a, hit.d);
}

function select(id: string | null): void {
  A.selected = id;
  A.lastPoseKey = "";
  A.host?.onSelect?.(id);
}

function showIdentify(a: ArAsset, d: number): void {
  const fix = A.host?.getFix() ?? null;
  const hd = getHeading();
  const cal = A.cal;
  const posCal = !!cal && !cal.anchored && cal.method !== "road" && cal.method !== "camera" && Math.abs(cal.range_shift_m) > 0;
  const unc = fix ? lateralUncertaintyM(d, effectiveGpsM(fix.accuracy, posCal), calCompassErr(hd.accuracy)) : a.uncertainty_m;
  // compact (2026-10-06: the card took half the screen): one summary line, details on demand
  openSheet(identifyCard({ ...a, distance_m: d, uncertainty_m: unc }, [], () => { select(null); closeSheet(); }, { compact: true }));
}

// --- calibrate on something you can see (no asset IDs) ----------------------
//
// tap (cal-tap) -> "what did you tap?" list + mini-map (cal-pick) -> "This
// one" -> PREVIEW: the overlay drawn with the candidate calibration, the
// shift in metres and degrees, a sanity check (cal-preview) -> Apply or
// Cancel. Applying keeps the previous calibration for "Undo" (UNDO_MS).

interface Ray {
  azimuth_deg: number;
  ground_range_m: number | null;
}

/** One calibration tap as two rays: `cur` in the CURRENT pose (any road /
 * earlier direction correction applied) for a position-only calibration,
 * which keeps that direction; `raw` in the uncalibrated pose for "Also
 * correct direction", where the server returns a whole new offset. */
interface CalRays {
  cur: Ray;
  raw: Ray;
}

async function calibrationTap(sx: number, sy: number): Promise<void> {
  if (!A.rawView || !A.view || !A.data) return;
  const fix = A.host?.getFix();
  if (!fix) return;
  const r = unproject(sx, sy, A.rawView);
  const c = unproject(sx, sy, A.view);
  await showCandidates({ cur: { azimuth_deg: c.azimuth_deg, ground_range_m: c.ground_range_m },
    raw: { azimuth_deg: r.azimuth_deg, ground_range_m: r.ground_range_m } }, fix, null);
}

interface CandidateList {
  list: ArCalCandidate[];
  auto: string | null;
  note: string | null;
  offline: boolean;
}

/** Marks candidates within CAL_CLOSE_PAIR_M of each other (the server does
 * the same; done here too for the offline ranking) and never auto-picks
 * one of a close pair. */
function markClose(res: CandidateList): CandidateList {
  const pairs = closePairs(res.list);
  res.list.forEach((c, i) => {
    c.close_to = (pairs.get(i) ?? []).map((j) => res.list[j].asset_id);
  });
  if (res.auto && res.list.find((c) => c.asset_id === res.auto)?.close_to?.length) res.auto = null;
  return res;
}

async function fetchCandidates(ray: Ray, fix: ArFix, kind: string | null): Promise<CandidateList> {
  const hd = getHeading();
  try {
    const res = await fieldApi.arCalCandidates({ lat: fix.lat, lon: fix.lon, accuracy: fix.accuracy, heading_accuracy: hd.accuracy,
      tap_azimuth_deg: ray.azimuth_deg, tap_ground_range_m: ray.ground_range_m, kind });
    return markClose({ list: res.candidates, auto: res.auto_pick, note: res.note, offline: false });
  } catch {
    // offline: the same ranking locally (arMath mirrors field_ar.calibration_candidates)
    const data = A.data;
    if (!data) return { list: [], auto: null, note: "Offline and no assets loaded.", offline: true };
    const [oe, on] = localOffset(data.origin.lat, data.origin.lon, fix.lat, fix.lon);
    const rel = data.assets.map((a) => ({ ...a, e: a.e !== undefined ? a.e - oe : undefined, n: a.n !== undefined ? a.n - on : undefined }));
    const ranked = rankCalibrationCandidates(rel, ray.azimuth_deg, ray.ground_range_m, fix.accuracy, hd.accuracy, kind);
    const tap = ray.ground_range_m === null ? null
      : [ray.ground_range_m * Math.sin((ray.azimuth_deg * Math.PI) / 180), ray.ground_range_m * Math.cos((ray.azimuth_deg * Math.PI) / 180)];
    const list: ArCalCandidate[] = ranked.map((c) => ({
      asset_id: c.a.asset_id, asset_type: c.a.asset_type, type_label: c.a.type_label, label: c.a.label, network: c.a.network,
      colour: c.a.colour, address: c.a.address, e: c.e, n: c.n, distance_m: c.distance_m, bearing_deg: enuBearing(c.e, c.n),
      angle_off_deg: c.angle_off_deg, range_off_m: ray.ground_range_m === null ? null : c.distance_m - ray.ground_range_m, score: c.score,
      tap_point_dist_m: tap ? Math.round(Math.hypot(c.e - tap[0], c.n - tap[1]) * 10) / 10 : null,
    }));
    const auto = list.length && (list.length === 1 || list[0].score >= 2 * list[1].score) && list[0].score >= 0.25 ? list[0].asset_id : null;
    return markClose({ list, auto, note: list.length ? null : "Nothing you could tap is mapped in that direction.", offline: true });
  }
}

const KINDS: [string | null, string][] = [[null, "Not sure"], ["lid", "Manhole / lid"], ["meter", "Meter / toby"], ["valve", "Valve"], ["hydrant", "Hydrant"]];

async function showCandidates(rays: CalRays, fix: ArFix, kind: string | null): Promise<void> {
  setMode("tapped");
  openSheet(h("p", {}, "Looking for what you tapped…"));
  // ranked on the current pose: position-only (the default) keeps its direction
  const ray = rays.cur;
  const res = await fetchCandidates(ray, fix, kind);
  if (A.mode !== "cal-pick") return; // cancelled while waiting
  const mini = h("canvas", { class: "fa-ar-minimap", width: 220, height: 220 }) as HTMLCanvasElement;
  const kinds = h("div", { class: "fa-chips" },
    h("span", { class: "fa-muted fa-small" }, "It's a:"),
    ...KINDS.map(([k, label]) => h("button", { class: `fa-chip${k === kind ? " on" : ""}`, onclick: () => void showCandidates(rays, fix, k) }, label)));
  const num = (id: string) => res.list.findIndex((c) => c.asset_id === id) + 1;
  const rows = res.list.map((c, i) => {
    const best = c.asset_id === res.auto;
    const side = c.angle_off_deg === 0 ? "" : `, ${Math.abs(Math.round(c.angle_off_deg))}° ${c.angle_off_deg > 0 ? "right" : "left"} of your tap`;
    const fromTap = c.tap_point_dist_m != null ? ` · ${c.tap_point_dist_m.toFixed(1)} m from your tap` : "";
    const close = c.close_to?.length
      ? h("small", { class: "fa-warn-inline" }, `⚠ within ${Math.round(Math.max(...c.close_to.map((id) => {
          const o = res.list.find((x) => x.asset_id === id)!;
          return Math.hypot(o.e - c.e, o.n - c.n);
        })) * 10) / 10} m of ${c.close_to.map((id) => `#${num(id)}`).join(", ")} - check the type`)
      : null;
    return h("li", { class: `fa-cal-cand${best ? " best" : ""}${c.close_to?.length ? " close" : ""}` },
      h("span", { class: "fa-cal-num" }, String(i + 1)),
      symbolIcon(c, 20),
      h("span", { class: "fa-cand-main" },
        h("strong", {}, `${best ? "Best match: " : ""}${c.type_label}`),
        h("small", {}, `${NETWORK_LABELS[c.network as keyof typeof NETWORK_LABELS] ?? ""}${c.address ? ` · ${c.address}` : ""} · ${Math.round(c.distance_m)} m away${side}${fromTap}`),
        close,
        h("small", { class: "fa-id" }, c.asset_id)),
      h("button", { class: `fa-btn${best ? " fa-primary" : ""}`, onclick: () => void previewCalibration(c, rays, fix, () => void showCandidates(rays, fix, kind)) }, "This one"));
  });
  const ambiguous = res.list.some((c) => c.close_to?.length);
  const zoom = ambiguous ? h("canvas", { class: "fa-ar-minimap fa-ar-minizoom", width: 140, height: 140 }) as HTMLCanvasElement : null;
  openSheet(
    h("strong", {}, "What did you tap?"),
    h("p", { class: "fa-muted fa-small" }, `Ranked by type and by how close each is to where your tap met the ground${res.offline ? " (offline ranking)" : ""}. Numbers match the little map.`),
    ambiguous ? h("p", { class: "fa-warn fa-small" }, "Some of these are within a couple of metres of each other - tap the type above (manhole, meter, valve…) or check the close-up map before choosing.") : null,
    kinds,
    res.list.length
      ? h("div", { class: "fa-cal-wrap" }, h("div", { class: "fa-cal-maps" }, mini, zoom), h("ol", { class: "fa-list fa-cal-list" }, ...rows))
      : h("p", { class: "fa-warn" }, res.note ?? "Nothing found."),
    h("div", { class: "fa-row" },
      h("button", { class: "fa-link", onclick: () => setMode("tap-again") }, "Tap somewhere else"),
      h("button", { class: "fa-link", onclick: () => setMode("cancel") }, "Cancel")),
  );
  if (res.list.length) drawMiniMap(mini, res.list, ray, null);
  if (zoom) {
    // close-up of the first close pair, ~8 px per metre, numbers fanned out
    const i = res.list.findIndex((c) => c.close_to?.length);
    const group = res.list.filter((c, k) => k === i || res.list[i].close_to!.includes(c.asset_id));
    const ce = group.reduce((s, c) => s + c.e, 0) / group.length, cn = group.reduce((s, c) => s + c.n, 0) / group.length;
    drawMiniMap(zoom, res.list, ray, { centre: [ce, cn], range: 6 });
  }
}

/** A plan view of the tap: you in the middle, camera up, the tapped direction
 * dashed, the tap's ground point ringed, candidates numbered (numbers fanned
 * out round candidates that sit close together), nearby pipes faint.
 * `zoomed`: a close-up centred on a point (metres E/N of you). */
function drawMiniMap(c: HTMLCanvasElement, list: ArCalCandidate[], ray: Ray, zoomed: { centre: [number, number]; range: number } | null): void {
  const ctx = c.getContext("2d");
  if (!ctx) return;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const S = zoomed ? 140 : 220;
  c.width = c.height = S * dpr;
  c.style.width = c.style.height = `${S}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const range = zoomed ? zoomed.range
    : Math.max(10, Math.min(45, Math.ceil((Math.max(...list.map((x) => x.distance_m), ray.ground_range_m ?? 0) * 1.25) / 5) * 5));
  const k = (S / 2 - 10) / range;
  const [ce, cn] = zoomed ? zoomed.centre : [0, 0];
  const up = ray.azimuth_deg * (Math.PI / 180); // rotate so the tapped direction is up
  const toXY = (e: number, n: number): [number, number] => {
    const de = e - ce, dn = n - cn;
    const x = de * Math.cos(up) - dn * Math.sin(up);
    const y = de * Math.sin(up) + dn * Math.cos(up);
    return [S / 2 + x * k, S / 2 - y * k];
  };
  ctx.fillStyle = "#0f1a24";
  ctx.fillRect(0, 0, S, S);
  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  if (!zoomed) {
    for (const f of [0.5, 1]) {
      ctx.beginPath();
      ctx.arc(S / 2, S / 2, (S / 2 - 10) * f, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  // nearby pipes, faint, network colours
  const data = A.data;
  const fix = A.host?.getFix();
  if (data && fix) {
    const [oe, on] = localOffset(data.origin.lat, data.origin.lon, fix.lat, fix.lon);
    ctx.lineWidth = 2;
    for (const a of data.assets) {
      if (a.kind !== "line" || !a.path_enu) continue;
      ctx.strokeStyle = hexA(assetColour(a), 0.45);
      ctx.beginPath();
      a.path_enu.forEach(([e, n], i) => {
        const [x, y] = toXY(e - oe, n - on);
        if (i) ctx.lineTo(x, y);
        else ctx.moveTo(x, y);
      });
      ctx.stroke();
    }
  }
  // the tapped direction, and where it met the ground
  const [ux, uy] = toXY(0, 0);
  ctx.setLineDash([5, 5]);
  ctx.strokeStyle = "#f6e05e";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(ux, uy);
  const far = toXY(Math.sin(up) * range * 3, Math.cos(up) * range * 3);
  ctx.lineTo(far[0], far[1]);
  ctx.stroke();
  ctx.setLineDash([]);
  if (ray.ground_range_m !== null) {
    const [tx, ty] = toXY(Math.sin(up) * ray.ground_range_m, Math.cos(up) * ray.ground_range_m);
    ctx.beginPath();
    ctx.arc(tx, ty, zoomed ? 7 : 5, 0, Math.PI * 2);
    ctx.stroke();
  }
  // candidates; a number sits beside its symbol, pushed out radially from
  // the centre of any close group so two lids a metre apart stay readable
  const pairs = closePairs(list);
  const pos = list.map((cand) => toXY(cand.e, cand.n));
  list.forEach((cand, i) => {
    const [x, y] = pos[i];
    drawSymbol(ctx, symbolOf(cand.asset_type), x, y, zoomed ? 9 : 8, assetColour(cand));
    const group = [i, ...(pairs.get(i) ?? [])];
    let dx = 1, dy = 0;
    if (group.length > 1) {
      const gx = group.reduce((s, j) => s + pos[j][0], 0) / group.length, gy = group.reduce((s, j) => s + pos[j][1], 0) / group.length;
      const len = Math.hypot(x - gx, y - gy);
      if (len > 0.5) {
        dx = (x - gx) / len;
        dy = (y - gy) / len;
      } else {
        const a = (group.indexOf(i) / group.length) * Math.PI * 2;
        dx = Math.cos(a);
        dy = Math.sin(a);
      }
    }
    const off = group.length > 1 ? 18 : 12;
    const lx = x + dx * off, ly = y + dy * off;
    if (group.length > 1) {
      ctx.strokeStyle = "rgba(255,255,255,0.7)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(lx, ly);
      ctx.stroke();
    }
    ctx.fillStyle = group.length > 1 ? "#f6e05e" : "#fff";
    ctx.beginPath();
    ctx.arc(lx, ly, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#0f1a24";
    ctx.font = "bold 10px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), lx, ly + 0.5);
  });
  // you (off the close-up's edge when zoomed)
  if (ux >= 0 && ux <= S && uy >= 0 && uy <= S) {
    ctx.fillStyle = "#4299e1";
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(ux, uy, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
  if (zoomed) {
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.font = "10px system-ui, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillText("close-up · 2 m", 6, S - 4);
    ctx.fillRect(6, S - 18, 2 * k, 2);
  }
}

function describe(c: ArCalCandidate): string {
  return `${c.type_label.toLowerCase()}${c.address ? ` at ${c.address}` : ""}`;
}

function signed(v: number, digits = 0): string {
  return `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(digits)}`;
}

function compass8(e: number, n: number): string {
  const pts = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return pts[Math.round((((Math.atan2(e, n) * 180) / Math.PI + 360) % 360) / 45) % 8];
}

/** The preview sheet: what applying would do, the sanity verdict, and
 * Apply / Apply anyway / Pick another / Cancel. The overlay behind it is
 * already drawn with `p.cal` (overlaysFor("cal-preview").previewCal). */
function showPreview(p: Preview): void {
  A.preview = p;
  A.lastPoseKey = "";
  const c = p.cal;
  const shift = Math.hypot(c.east_m, c.north_m);
  const s = p.sanity;
  const apply = () => applyPreview();
  const lidToggle = p.lid
    ? h("label", { class: "fa-row-check fa-small" },
        h("input", { type: "checkbox", class: "fa-check", checked: p.lid.mode === "full", onchange: () => p.lid?.toggle() }),
        h("span", {}, "Also correct direction (off: the tap fixes where you stand; direction stays with the compass / road)"))
    : null;
  const roads = p.roads
    ? h("div", { class: "fa-ar-roads" },
        h("p", { class: "fa-small" }, h("strong", {}, `Aligning to ${p.roads.name}`)),
        p.roads.alternatives.length
          ? h("div", { class: "fa-chips" },
              h("span", { class: "fa-muted fa-small" }, "Not this road?"),
              ...p.roads.alternatives.map((a) => h("button", { class: "fa-chip", onclick: () => p.roads?.pick(a.key) },
                `${a.name} (${Math.round(a.distance_m)} m${a.off_axis_deg !== null ? `, ${Math.round(a.off_axis_deg)}° off your view` : ""})`)))
          : null)
    : null;
  if (p.direct) {
    const ds = p.direct;
    const tm = Math.hypot(ds.t[0], ds.t[1]);
    const move = tm >= 0.05 ? `Moves your position ${tm.toFixed(1)} m ${compass8(ds.t[0], ds.t[1])}` : "Leaves your position as it is";
    const line = Math.abs(ds.rot) >= 0.05
      ? `${move} and turns the compass ${signed(ds.rot, 1)}°.`
      : `${move}. Direction unchanged (from the ${c.heading_from ?? "compass"}).`;
    openSheet(
      h("strong", {}, p.title),
      h("p", { class: "fa-small" }, line),
      ds.mode === "two_point" && ds.residual !== null
        ? h("p", { class: "fa-small" }, `The two points agree to ${ds.residual.toFixed(1)} m (they are ${(ds.span ?? 0).toFixed(0)} m apart).`) : null,
      ds.mode !== "two_point"
        ? h("p", { class: "fa-muted fa-small" }, "Drag a second thing onto its real spot to fix the direction too, or twist with two fingers.") : null,
      s.message ? h("p", { class: s.level === "refuse" ? "fa-warn fa-small" : "fa-warn-inline fa-small" }, `⚠ ${s.message}`) : null,
      ds.checked === "phone" && ds.serverNote ? h("p", { class: "fa-muted fa-small" }, ds.serverNote) : null,
      h("div", { class: "fa-row" },
        s.level === "ok" ? h("button", { class: "fa-btn fa-primary", onclick: apply }, "Apply") : null,
        s.level === "warn" ? h("button", { class: "fa-btn", onclick: apply }, "Apply anyway") : null,
        h("button", { class: "fa-link", onclick: () => setMode("cancel") }, "Cancel")));
    return;
  }
  openSheet(
    h("strong", {}, p.title),
    roads,
    h("p", { class: "fa-small" },
      p.lid
        ? lidPreviewText(p.lid.mode, c.east_m, c.north_m, c.heading_offset_deg, c.heading_from ?? "compass")
        : shift >= 0.05
          ? `Moves your position ${shift.toFixed(1)} m ${compass8(c.east_m, c.north_m)} (${signed(c.east_m, 1)} m E, ${signed(c.north_m, 1)} m N) and turns the compass ${signed(c.heading_offset_deg)}°.`
          : `Turns the compass ${signed(c.heading_offset_deg)}° (position unchanged).`),
    lidToggle,
    s.move_m > 0 ? h("p", { class: "fa-muted fa-small" }, `The asset you picked moves ${s.move_m.toFixed(1)} m on the ground (GPS allows about ${s.limit_m.toFixed(0)} m here).`) : null,
    h("p", { class: "fa-muted fa-small" }, "The overlay behind this card shows the result. Does the pipe / lid now sit where you see it?"),
    p.guide ? h("p", { class: "fa-muted fa-small" }, "Yellow dashed lines: where the kerbs should be after this (LINZ road centreline ± the carriageway half-width). They should sit on the real kerbs - if they don't, Cancel.") : null,
    s.message ? h("p", { class: s.level === "refuse" ? "fa-warn" : "fa-warn fa-small" }, `⚠ ${s.message}`) : null,
    h("div", { class: "fa-row" },
      s.level === "ok" ? h("button", { class: "fa-btn fa-primary", onclick: apply }, "Apply") : null,
      s.level === "warn" ? h("button", { class: "fa-btn", onclick: apply }, "Apply anyway") : null,
      p.directionOnly && headingSanity(c.heading_offset_deg).level !== "refuse"
        ? h("button", { class: "fa-btn", onclick: () => applyPreview(true) }, "Direction only") : null,
      p.back ? h("button", { class: "fa-btn", onclick: () => p.back?.() }, "Pick another") : null,
      h("button", { class: "fa-link", onclick: () => setMode("cancel") }, "Cancel")),
  );
}

function applyPreview(directionOnly = false): void {
  const p = A.preview;
  if (!p || (p.sanity.level === "refuse" && !directionOnly)) return;
  const prev = A.cal;
  let cal: StoredCal = { ...p.cal, at: Date.now() };
  if (directionOnly) cal = { ...cal, east_m: 0, north_m: 0, range_shift_m: 0, lateral_shift_m: 0, normal: null, label: `${cal.label ?? "the kerb"}, direction only` };
  // 2026-10-05: the position part becomes an ANCHOR in the tracker ("right
  // now I am truly here"), which then follows your steps from there and
  // fades back to GPS with time and distance - instead of a fixed offset
  // re-applied to every new raw GPS fix.
  let token: AnchorToken | null = null;
  const sigma = cal.sigma_m ?? 1;
  if (cal.normal && cal.lateral_shift_m) {
    token = anchorAlong(cal.normal[0], cal.normal[1], cal.lateral_shift_m, sigma);
  } else if (Math.hypot(cal.east_m, cal.north_m) >= 0.05) {
    token = anchorShift(cal.east_m, cal.north_m, sigma);
  }
  // never add the shift on top again (heading-only: there is none); if the
  // tracker had no fix yet the shift could not be anchored and is dropped
  cal.anchored = true;
  cal.pos_anchor = !!token;
  saveCal(cal);
  A.undo = { prev, until: Date.now() + UNDO_MS, label: "Undo last calibration", anchor: token };
  setMode("apply");
  // a toast-like inline "Undo" for a few seconds, then it tucks into the status pill's sheet
  const moved = Math.hypot(cal.east_m, cal.north_m);
  const turned = Math.abs(cal.heading_offset_deg - (prev?.heading_offset_deg ?? 0)) >= 0.5;
  offerUndo(`Calibrated: ${moved >= 0.05 ? `${moved.toFixed(1)} m` : "direction"}${turned ? `, ${signed(cal.heading_offset_deg)}°` : ""}`);
}

async function previewCalibration(c: ArCalCandidate, rays: CalRays, fix: ArFix, back: () => void, mode: CalMode = "position"): Promise<void> {
  // position only (default): the ray in the current pose, the direction kept;
  // "Also correct direction": the raw ray, a whole new heading offset
  const ray = mode === "position" ? rays.cur : rays.raw;
  let cal: Calibration & { ttl_s?: number; max_move_m?: number };
  try {
    cal = await fieldApi.arCalibrate({ lat: fix.lat, lon: fix.lon, accuracy: fix.accuracy, asset_id: c.asset_id,
      tap_azimuth_deg: ray.azimuth_deg, tap_ground_range_m: ray.ground_range_m, mode });
  } catch {
    // offline: the same maths locally (arMath.calibrationOffset mirrors field_ar.py)
    cal = calibrationOffset(c.e, c.n, ray.azimuth_deg, ray.ground_range_m, fix.accuracy, mode);
  }
  if (A.mode !== "cal-pick" && A.mode !== "cal-preview") return; // cancelled while waiting
  if (!cal.accepted) {
    showToast(cal.reason ?? "That doesn't look like the thing you tapped.");
    return;
  }
  // c.e / c.n are relative to this fix (server and offline alike)
  const sanity = calibrationSanity(c.e, c.n, ray.azimuth_deg, ray.ground_range_m, mode === "full" ? cal.heading_offset_deg : 0, fix.accuracy, mode);
  const [alat, alon] = new LocalFrame(fix.lat, fix.lon).toLatLon(c.e, c.n);
  const short = c.type_label.toLowerCase();
  // a road / kerb direction in force survives a position-only lid calibration
  const active = currentCalibration(fix);
  const dir = lidDirection(mode, active ? { heading_offset_deg: active.heading_offset_deg, heading_from: active.heading_from ?? active.label ?? null } : null,
    cal.heading_offset_deg, short);
  const stored: StoredCal = { ...cal, heading_offset_deg: dir.heading_offset_deg, heading_from: dir.heading_from, mode,
    at: Date.now(), lat: fix.lat, lon: fix.lon, asset_id: c.asset_id, method: "lid", label: describe(c),
    ttl_s: cal.ttl_s ?? A.data?.calibration.ttl_s ?? 900, max_move_m: cal.max_move_m ?? A.data?.calibration.max_move_m ?? 40,
    short, anchor_lat: alat, anchor_lon: alon, sigma_m: calibrationSigmaM(c.distance_m) };
  setMode("picked");
  const other: CalMode = mode === "position" ? "full" : "position";
  showPreview({ cal: stored, sanity, title: `Calibrate on the ${describe(c)}?`, back,
    lid: { mode, toggle: () => void previewCalibration(c, rays, fix, back, other) } });
  if (cal.clamped) A.sheet?.children[0]?.after(h("p", { class: "fa-warn fa-small" }, "The move was capped at what GPS can plausibly be out - check the overlay."));
}

// --- line up with the road ---------------------------------------------------

function confirmHeadingFix(fix: ArFix, res: ArHeadingFix, title: string, detail: string[], kerbMid?: [number, number] | null,
  pick?: (key: string) => void): void {
  const kerb = res.method === "kerb";
  const cal: StoredCal = { heading_offset_deg: res.heading_offset_deg, east_m: kerb ? res.east_m : 0, north_m: kerb ? res.north_m : 0,
    range_shift_m: kerb ? res.range_shift_m : 0, accepted: true,
    at: Date.now(), lat: fix.lat, lon: fix.lon, ttl_s: res.ttl_s ?? 900, max_move_m: res.max_move_m ?? 40,
    method: res.method,
    label: res.method === "road" ? res.road?.name ?? "the road" : kerb ? `the kerb of ${res.road?.name ?? "the road"}` : `the kerb (camera, ${res.road?.name ?? "road"})`,
    short: kerb ? "the kerb" : undefined,
    normal: kerb ? res.normal ?? null : null, lateral_shift_m: kerb ? res.lateral_shift_m : undefined,
    sigma_m: kerb ? res.position_sigma_m : undefined,
    heading_from: `${kerb ? "kerb" : "road"} (${res.road?.name ?? "the road"})`,
    anchor_lat: kerbMid ? kerbMid[0] : undefined, anchor_lon: kerbMid ? kerbMid[1] : undefined };
  setMode("heading-result");
  const sanity = kerb ? kerbSanity(res.lateral_shift_m ?? 0, res.heading_offset_deg, fix.gpsAccuracy ?? fix.accuracy) : headingSanity(res.heading_offset_deg);
  const guide = res.kerb_lines_enu?.length ? { lat: fix.lat, lon: fix.lon, lines: res.kerb_lines_enu } : undefined;
  showPreview({ cal, sanity, title, guide, directionOnly: kerb, roads: pick ? { name: res.road?.name ?? "the road", alternatives: res.alternatives ?? [], pick } : undefined });
  // the road / camera specifics under the shift line
  if (A.sheet) {
    const extra = [...detail.map((t) => h("p", { class: "fa-small" }, t)), res.warning ? h("p", { class: "fa-warn" }, res.warning) : null]
      .filter((x): x is HTMLParagraphElement => x !== null);
    A.sheet.children[0]?.after(...extra);
  }
}
async function alignToRoad(roadKey: string | null = null): Promise<void> {
  // "Not this road?" re-runs the same request (same fix and heading) with another road
  const again = roadKey !== null && A.lastRoad !== null;
  const fix = again ? A.lastRoad!.fix : A.host?.getFix();
  const hd = getHeading();
  const heading = again ? A.lastRoad!.heading : hd.heading;
  if (!fix || heading === null) {
    showToast("Needs GPS and the compass.");
    return;
  }
  A.lastRoad = { fix, heading };
  A.busy = true;
  renderPanel();
  try {
    let res = await fieldApi.arAlignRoad({ lat: fix.lat, lon: fix.lon, accuracy: fix.accuracy, camera_heading_deg: heading, road_key: roadKey });
    // refused, but another road nearby does run along the view: try that one
    // (the preview names it and offers the first one back)
    const along = !res.accepted && roadKey === null ? (res.alternatives ?? []).find((a) => a.off_axis_deg !== null && a.off_axis_deg <= 30) : undefined;
    if (along) res = await fieldApi.arAlignRoad({ lat: fix.lat, lon: fix.lon, accuracy: fix.accuracy, camera_heading_deg: heading, road_key: along.key });
    if (!res.accepted) {
      showToast(res.reason ?? "Couldn't line up with the road here.");
      return;
    }
    const ac = autoRoadConfidence(res.confidence, { warning: null });
    const conf = `${ac.level} (${ac.pct}%)${ac.suggestKerb ? " - tapping the kerb in two places is more reliable" : ""}`;
    confirmHeadingFix(fix, res, `Line up with ${res.road?.name ?? "the road"}`, [
      `${res.road?.name} runs ${Math.round(res.road?.direction_deg ?? 0)}° here (LINZ road centreline, ${res.road?.distance_m} m from you).`,
      `Your compass reads ${Math.round(heading)}°, so it is about ${Math.abs(res.heading_offset_deg).toFixed(0)}° ${res.heading_offset_deg > 0 ? "low" : "high"}. Confidence: ${conf}.`,
      "This fixes the direction only, not where you stand.",
    ], null, (key) => void alignToRoad(key));
  } catch {
    showToast("Couldn't reach the server - road alignment needs a signal.");
  } finally {
    A.busy = false;
    renderPanel();
  }
}

// --- two taps on the kerb: direction + position across the road -------------
//
// Tap the near kerb close by, then further along. Each tap's ground point is
// kept in the RAW pose (what the server needs: it returns a whole new
// heading offset plus a shift across the road) and in the calibrated view
// (what is drawn, so the marker stays where you tapped).

const KERB_MAX_TAP_M = 25;

async function kerbTap(sx: number, sy: number): Promise<void> {
  if (!A.rawView || !A.view || A.busy) return;
  const raw = unproject(sx, sy, A.rawView);
  const drawn = unproject(sx, sy, A.view);
  if (raw.ground_range_m === null || drawn.ground_range_m === null || raw.ground_range_m > KERB_MAX_TAP_M) {
    showToast("Tap the kerb on the ground, within about 25 m (tilt the phone down a little).");
    return;
  }
  const at = (r: typeof raw, e0: number, n0: number): [number, number] => {
    const a = (r.azimuth_deg * Math.PI) / 180;
    return [e0 + (r.ground_range_m as number) * Math.sin(a), n0 + (r.ground_range_m as number) * Math.cos(a)];
  };
  A.kerbTaps.push({ raw: at(raw, A.rawDevE, A.rawDevN), draw: at(drawn, A.devE, A.devN) });
  setMode("tapped"); // stays "kerb"
  if (A.kerbTaps.length < 2) return;
  const [k1, k2] = A.kerbTaps;
  if (Math.hypot(k2.raw[0] - k1.raw[0], k2.raw[1] - k1.raw[1]) < 2) {
    A.kerbTaps.pop();
    showToast("The second tap is too close to the first - tap the same kerb further along (5-15 m ahead).");
    return;
  }
  await alignToKerb();
}

async function alignToKerb(roadKey: string | null = null): Promise<void> {
  const fix = A.host?.getFix();
  const data = A.data;
  if (!fix || !data || A.kerbTaps.length < 2) return;
  A.lastKerb = { taps: [...A.kerbTaps] };
  // the taps relative to where the phone is NOW (it may have moved a little between them)
  const [oe, on] = localOffset(data.origin.lat, data.origin.lon, fix.lat, fix.lon);
  const [k1, k2] = A.kerbTaps;
  const mid = new LocalFrame(data.origin.lat, data.origin.lon).toLatLon((k1.draw[0] + k2.draw[0]) / 2, (k1.draw[1] + k2.draw[1]) / 2);
  A.busy = true;
  renderPanel();
  try {
    const res = await fieldApi.arAlignKerb({ lat: fix.lat, lon: fix.lon, accuracy: fix.gpsAccuracy ?? fix.accuracy,
      p1_e: k1.raw[0] - oe, p1_n: k1.raw[1] - on, p2_e: k2.raw[0] - oe, p2_n: k2.raw[1] - on, road_key: roadKey });
    if (A.mode !== "kerb") return; // cancelled while waiting
    if (!res.accepted) {
      A.kerbTaps = [];
      A.lastPoseKey = "";
      showToast(res.reason ?? "Couldn't match that kerb to a mapped road. Try again.");
      setInfo();
      renderPanel();
      return;
    }
    A.kerbTaps = [];
    const conf = res.confidence >= 0.75 ? "good" : res.confidence >= 0.45 ? "fair" : "rough";
    const lat = res.lateral_shift_m ?? 0;
    confirmHeadingFix(fix, res, `Calibrate on the kerb of ${res.road?.name ?? "the road"}?`, [
      `${res.road?.name} runs ${Math.round(res.road?.direction_deg ?? 0)}° here (LINZ road centreline). The kerb you tapped is ${res.kerb?.distance_m ?? "?"} m from you; ${res.road?.lane_count ? `${res.road.lane_count} lane(s)` : "lanes not recorded"}, so the middle of the road is taken as ${res.road?.half_width_m ?? "?"} m beyond it.`,
      `Turns the compass ${signed(res.heading_offset_deg)}° and moves you ${Math.abs(lat).toFixed(1)} m across the road (nothing along it - a straight kerb can't tell). Position good to about ±${(res.position_sigma_m ?? 2).toFixed(1)} m across. Confidence: ${conf}.`,
      "If the yellow kerb lines look wrong (a wide road, parking bays, the centreline off), use “Direction only”.",
    ], mid, (key) => {
      // "Not this road?": the same two taps against another road
      const taps = A.lastKerb?.taps ?? [];
      setMode("cancel");
      A.kerbTaps = [];
      setMode("menu-kerb");
      A.kerbTaps = taps;
      void alignToKerb(key);
    });
  } catch {
    A.kerbTaps = [];
    showToast("Couldn't reach the server - kerb calibration needs a signal.");
  } finally {
    A.busy = false;
    renderPanel();
  }
}

// --- "Line up with the road": camera-assisted (2026-10-05) -----------------
//
// The manual guides only matched if you stood in the middle of the road; on
// a real street there are parked cars both sides and traffic. Now the phone
// sends ONE frame + its pose and the server (field_ar_roadlines: vehicles
// masked, RANSAC vanishing point against the LINZ road direction) proposes
// the kerb: heading AND a shift across the road. Every line it found is
// drawn over the camera where it was seen; tap another to use it (the far
// kerb, say), change what the line is, or fall back to the manual guides.
// Replaces "Auto-align from camera (experimental)". The frame is never stored.

const ROLE_LABELS: Record<ArRoadRole, string> = {
  near_kerb: "the kerb on my side",
  far_kerb: "the far kerb",
  centre_line: "the centre line",
  edge: "not a road edge (direction only)",
};

let roadAutoTimer: number | null = null;
/** the last automatic look failed or was low-confidence: the road panel then leads with the kerb taps */
let autoWeak = false;

/** The default calibration (2026-10-08): two taps on the kerb. */
function startKerbMode(): void {
  if (roadAutoTimer !== null) window.clearTimeout(roadAutoTimer);
  roadAutoTimer = null;
  autoWeak = false;
  if (A.mode !== "view") setMode("cancel");
  A.kerbTaps = [];
  setMode("menu-kerb");
}

function startRoadMode(): void {
  autoWeak = false;
  setMode("menu-road");
  // feels automatic: one look as soon as the view has settled
  if (roadAutoTimer !== null) window.clearTimeout(roadAutoTimer);
  roadAutoTimer = window.setTimeout(() => {
    roadAutoTimer = null;
    if (A.mode === "road" && !A.busy) void findRoadLines(true);
  }, 1200);
}

async function grabFrame(): Promise<Blob | null> {
  const video = A.video;
  if (!video || !video.videoWidth) return null;
  const s = Math.min(1, 1280 / Math.max(video.videoWidth, video.videoHeight));
  const c = document.createElement("canvas");
  c.width = Math.round(video.videoWidth * s);
  c.height = Math.round(video.videoHeight * s);
  c.getContext("2d")?.drawImage(video, 0, 0, c.width, c.height);
  return await new Promise<Blob | null>((res) => c.toBlob((b) => res(b), "image/jpeg", 0.8));
}

async function findRoadLines(auto = false, again: { rp: RoadPick; key: string } | null = null): Promise<void> {
  const fix = again ? again.rp.fix : A.host?.getFix();
  const hd = getHeading();
  const data = A.data;
  if (!fix || !data || (!again && (hd.heading === null || hd.pitch === null || hd.roll === null))) {
    if (!auto) showToast("Needs GPS, the compass and the camera.");
    return;
  }
  // the pose at the moment of capture (raw compass: the server returns a whole new offset);
  // "Not this road?" re-sends the same frame and pose with the other road
  const pose = again?.rp.pose ?? { heading: hd.heading as number, beta: (hd.pitch as number) + 90, gamma: hd.roll as number, screen_angle: screenAngle() };
  const [capE, capN] = again ? [again.rp.capE, again.rp.capN] : localOffset(data.origin.lat, data.origin.lon, fix.lat, fix.lon);
  const frame = again?.rp.frame ?? await grabFrame();
  if (!frame) {
    if (!auto) showToast("The camera isn't ready yet.");
    return;
  }
  A.busy = true;
  renderPanel();
  try {
    const res = await fieldApi.arRoadLines(frame, { lat: fix.lat, lon: fix.lon, accuracy: fix.gpsAccuracy ?? fix.accuracy, ...pose, fov_deg: A.fov,
      road_key: again?.key ?? null });
    if (A.mode !== "road") return; // cancelled while waiting
    if (!res.candidates.length || res.best === null) {
      const on = res.road?.name ? `${res.road.name}: ` : "";
      autoWeak = true;
      setInfo(`${res.reason ?? "No kerb found."} Tap the kerb in two places instead - it's more reliable.`);
      showToast(`${on}${res.reason ?? "No kerb or lane line found."} Automatic didn't work here: tap the kerb in two places instead.`);
      return;
    }
    const best = res.candidates.find((c) => c.id === res.best) ?? res.candidates[0];
    setMode("heading-result");
    showRoadPreview({ res, fix, capE, capN, selected: best.id, role: best.role, frame, pose });
  } catch (e) {
    showToast(`Couldn't look for the kerb: ${(e as Error).message || "no signal"}. The manual guides work offline-ish (they need the road data once).`);
  } finally {
    A.busy = false;
    renderPanel();
  }
}

/** A role for a line the server called "edge" when the user picks it. */
function roleForOther(c: ArRoadLines["candidates"][number], res: ArRoadLines): ArRoadRole {
  const sc = res.centreline_lateral_m ?? 0;
  const hw = res.road?.half_width_m ?? 4.75;
  if (sc !== 0 && Math.sign(c.lateral_m) === Math.sign(sc)) return Math.abs(c.lateral_m) > 1.3 * hw ? "far_kerb" : "near_kerb";
  return "edge";
}

function showRoadPreview(rp: RoadPick): void {
  const { res, fix } = rp;
  const c = res.candidates.find((x) => x.id === rp.selected) ?? res.candidates[0];
  const pos = rp.role !== "edge" && !!res.normal;
  const delta = pos ? c.implied_shift_m[rp.role as Exclude<ArRoadRole, "edge">] : 0;
  const nrm = res.normal ?? [0, 0];
  const data = A.data;
  // where the line is, for the amber bar's "anchored to the kerb 6 m away"
  const mid = data ? new LocalFrame(data.origin.lat, data.origin.lon).toLatLon(
    rp.capE + (c.ground_enu_raw[0][0] + c.ground_enu_raw[1][0]) / 2, rp.capN + (c.ground_enu_raw[0][1] + c.ground_enu_raw[1][1]) / 2) : null;
  const cal: StoredCal = { heading_offset_deg: res.heading_offset_deg, east_m: delta * nrm[0], north_m: delta * nrm[1],
    range_shift_m: Math.abs(delta), accepted: true, at: Date.now(), lat: fix.lat, lon: fix.lon,
    ttl_s: res.ttl_s ?? 900, max_move_m: res.max_move_m ?? 40, method: "road",
    label: `${res.road?.name ?? "the road"} (camera: ${pos ? ROLE_LABELS[rp.role] : "direction"})`,
    heading_from: `road (${res.road?.name ?? "the road"}, camera)`,
    short: pos ? (rp.role === "centre_line" ? "the centre line" : "the kerb") : undefined,
    normal: pos ? (res.normal as [number, number]) : null, lateral_shift_m: pos ? delta : undefined,
    sigma_m: pos ? c.position_sigma_m : undefined,
    anchor_lat: mid ? mid[0] : undefined, anchor_lon: mid ? mid[1] : undefined };
  const sanity = pos ? kerbSanity(delta, res.heading_offset_deg, fix.gpsAccuracy ?? fix.accuracy) : headingSanity(res.heading_offset_deg);
  const guide = res.kerb_lines_enu?.length ? { lat: fix.lat, lon: fix.lon, lines: res.kerb_lines_enu } : undefined;
  const ac = autoRoadConfidence(res.confidence, { warning: res.warning ?? null, uncertaintyDeg: res.uncertainty_deg ?? null });
  const conf = `${ac.level} (${ac.pct}%)`;
  autoWeak = ac.suggestKerb;
  const side = c.lateral_m >= 0 ? "right" : "left";
  const roads = { name: res.road?.name ?? "the road", alternatives: res.alternatives ?? [],
    pick: (key: string) => { setMode("cancel"); setMode("menu-road"); void findRoadLines(false, { rp, key }); } };
  showPreview({ cal, sanity, title: `Line up with ${res.road?.name ?? "the road"}`, guide, directionOnly: pos, road: rp, roads });
  if (!A.sheet) return;
  const roles = (["near_kerb", "far_kerb", "centre_line", "edge"] as ArRoadRole[]).map((r) =>
    h("button", { class: `fa-chip${r === rp.role ? " on" : ""}`, onclick: () => showRoadPreview({ ...rp, role: r }) }, ROLE_LABELS[r]));
  const meter = h("div", { class: `fa-conf fa-conf-${ac.level}`, role: "meter", "aria-valuemin": "0", "aria-valuemax": "100",
    "aria-valuenow": String(ac.pct), "aria-label": "Automatic calibration confidence" },
    h("span", { class: "fa-conf-bar" }, h("span", { class: "fa-conf-fill", style: `width:${ac.pct}%` })),
    h("span", { class: "fa-small" }, ac.text));
  const extra: (HTMLElement | null)[] = [
    meter,
    ac.suggestKerb
      ? h("div", { class: "fa-row fa-conf-suggest" },
          h("button", { class: "fa-btn fa-primary", onclick: () => startKerbMode() }, "Tap the kerb in two places instead"))
      : null,
    h("p", { class: "fa-small" },
      `Found ${res.candidates.length} line(s) along the street. Using line ${c.id + 1} (yellow): ${Math.abs(c.lateral_m).toFixed(1)} m to your ${side}, seen ${c.forward_m[0].toFixed(0)}-${c.forward_m[1].toFixed(0)} m ahead. ` +
      `Compass ${signed(res.heading_offset_deg)}°${res.uncertainty_deg !== undefined ? ` (±${Math.max(1, Math.round(res.uncertainty_deg))}°)` : ""}` +
      (pos ? `, and ${Math.abs(delta).toFixed(1)} m across the road (±${c.position_sigma_m.toFixed(1)} m)` : ", direction only") +
      `. Confidence: ${conf}.`),
    h("div", { class: "fa-chips" }, h("span", { class: "fa-muted fa-small" }, "Line " + String(c.id + 1) + " is:"), ...roles),
    h("p", { class: "fa-muted fa-small" }, "Tap another white line on the camera to use it instead. The yellow dashed lines are where the map says the kerbs are once applied: they should land on the real kerbs."),
    res.warning ? h("p", { class: "fa-warn fa-small" }, res.warning) : null,
    h("div", { class: "fa-row" },
      h("button", { class: "fa-link", onclick: () => setMode("manual-guides") }, "Use the manual guides instead"),
      h("button", { class: "fa-link", onclick: () => { setMode("cancel"); startRoadMode(); } }, "Look again")),
  ];
  A.sheet.children[0]?.after(...extra.filter((x): x is HTMLElement => x !== null));
}
